import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import * as Sentry from '@sentry/react';
import {
  CustomNode,
  NodeStruct,
  NodeType,
  InputValue,
  GroupedNodes,
  NodeCategory,
  ApiNodeData,
  NodeData,
  NodeResult,
  NodeInputPort,
  NodeOutputPort,
  StickyNoteColor,
  StickyNoteData,
  DEFAULT_STICKY_NOTE_COLOR,
  createStickyNoteStruct,
  STICKY_NOTE_DEFAULT_WIDTH,
  STICKY_NOTE_DEFAULT_HEIGHT,
  STICKY_NOTE_Z_INDEX,
  SELECT_NODE_PLACEHOLDER_STRUCT,
} from '@/types/node-types';
import { ConnectionType, CustomEdge } from '@/types/edge-types';
import { useEdgeLayoutStore } from '@/stores/edge-layout-store';
import {
  addEdge,
  Connection,
  applyNodeChanges,
  applyEdgeChanges,
  NodeChange,
  EdgeChange,
  reconnectEdge,
  NodeDimensionChange,
  SetCenter,
} from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import { getNodeList } from '@/services/workflow/get-node-list';
import { isPickerHiddenNode } from '@/constants/node-catalog';
import { useOrgStore } from '@/stores/org-store';
import { cleanupWorkflow } from '@/pages/workflow/helper/workflow-cleanup';
import {
  repositionArtifactNodes,
  isArtifactEdge,
  isArtifactNode,
} from '@/pages/workflow/helper/artifact-node-manager';
import {
  expandGroupNodes,
  getAbsolutePosition,
  promoteGroupChildrenOnDelete,
  syncGroupNodeInteractionState,
  isGroupNode,
} from '@/pages/workflow/helper/group-nodes';
import { isBlankValue } from '@/stores/helpers/required-value';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import {
  generateAutoNodeName,
  isNameParameter,
} from '@/stores/helpers/node-naming';
import { DnDPayload } from '@/context/dnd-context';
import { isCatalogVersionNewer } from '@/pages/workflow/helper/node-version';
import {
  applyAutoConnection,
  calculateDropPosition,
  resolveAutoConnection,
  VERTICAL_STACK_GAP,
} from '@/stores/helpers/node-drop-helpers';

export interface NodeUpdateAvailable {
  id: string;
  nodeId: string;
  nodeName: string;
  nodeStructId: string;
}

const CLIPBOARD_STORAGE_KEY = 'workflow-clipboard';

// Module-level depth counter to suppress saveSnapshot during batch operations
// (e.g. multi-node auto-connect) so they become a single undo step. A counter
// rather than a flag so overlapping suppression windows (e.g. two chat-applied
// mutation cards each spanning an async auto-layout) nest correctly instead of
// one's early end re-enabling snapshots while the other is still in flight.
let snapshotSuppressionDepth = 0;

/**
 * Runs `fn` with saveSnapshot suppressed. During batch operations like
 * drop-onto-node, each onConnect call normally saves its own snapshot,
 * creating N intermediate undo steps. This wrapper prevents those
 * intermediates so the entire batch is one atomic undo.
 */
export function withSuppressedSnapshots(fn: () => void) {
  snapshotSuppressionDepth++;
  try {
    fn();
  } finally {
    snapshotSuppressionDepth--;
  }
}

/**
 * Begin suppressing snapshots across async boundaries (e.g. mutation apply
 * followed by a delayed auto-layout). Must be paired with endSnapshotSuppression
 * to re-enable snapshot saving.
 */
export function beginSnapshotSuppression() {
  snapshotSuppressionDepth++;
}

export function endSnapshotSuppression() {
  snapshotSuppressionDepth = Math.max(0, snapshotSuppressionDepth - 1);
}

// Default node width used for positioning when measured width is not yet available
// This represents the typical width of a node before React Flow measures it
const DEFAULT_NODE_WIDTH = 420;
const DEFAULT_NODE_HEIGHT = 200;

// Horizontal gap between nodes when auto-positioning subsequent nodes
// This spacing ensures nodes are visually separated and connections are clear
const NODE_HORIZONTAL_GAP = 20;

// Animation duration in milliseconds when auto-focusing on a newly added node
// This provides a smooth, noticeable pan animation without being too slow
const AUTO_FOCUS_ANIMATION_DURATION_MS = 500;

// Helper functions for save clipboard to localStorage
const saveClipboardToStorage = (nodes: CustomNode[], edges: CustomEdge[]) => {
  try {
    const clipboardData = {
      nodes,
      edges,
    };
    localStorage.setItem(CLIPBOARD_STORAGE_KEY, JSON.stringify(clipboardData));
  } catch (error) {
    console.error('Failed to save clipboard to localStorage:', error);
    Sentry.captureException(error, {
      extra: { context: 'save-clipboard-to-localStorage' },
    });
  }
};

// Helper functions to check if any outputPorts is compatible with any inputPorts
const checkForAnyCompatiblePorts = (
  outputPorts: NodeOutputPort[],
  inputPorts: NodeInputPort[],
) => {
  const isCompatible = outputPorts.some((outputPort) => {
    const sourceTypes = outputPort.port_type.accepted_types;

    return inputPorts.some((inputPort) => {
      const targetTypes = inputPort.port_type.accepted_types;

      // Check for type overlap
      return sourceTypes.some((sourceType) => targetTypes.includes(sourceType));
    });
  });

  return isCompatible;
};

export const loadClipboardFromStorage = (): {
  nodes: CustomNode[];
  edges: CustomEdge[];
} => {
  try {
    const savedClipboard = localStorage.getItem(CLIPBOARD_STORAGE_KEY);
    if (savedClipboard) {
      const { nodes, edges } = JSON.parse(savedClipboard);
      return { nodes: nodes || [], edges: edges || [] };
    }
  } catch (error) {
    console.error('Failed to load clipboard from localStorage:', error);
    Sentry.captureException(error, {
      extra: { context: 'load-clipboard-from-localStorage' },
    });
  }
  return { nodes: [], edges: [] };
};

// Helper function to detect edge type based on target handle and node params
// Returns PORT_TO_PARAM if the handle matches a parameter's bindable_config input_port,
// otherwise returns PORT_TO_PORT
const detectEdgeType = (
  targetHandle: string | null | undefined,
  targetNodeParams: NodeStruct['params'] | undefined,
): { edgeType: ConnectionType; targetParamId: string | null } => {
  if (!targetHandle || !targetNodeParams) {
    return { edgeType: ConnectionType.PORT_TO_PORT, targetParamId: null };
  }

  for (const param of targetNodeParams) {
    if (targetHandle === param.bindable_config?.input_port?.id) {
      return {
        edgeType: ConnectionType.PORT_TO_PARAM,
        targetParamId: param.field_id,
      };
    }
  }

  return { edgeType: ConnectionType.PORT_TO_PORT, targetParamId: null };
};

// Helper function to create a node with proper ID synchronization
// SelectNode does not use this
const createNodeWithSyncedId = (
  nodeStruct: NodeStruct,
  position: { x: number; y: number },
  existingNodes: CustomNode[],
  options: {
    selected?: boolean;
    nodeId?: string;
  } = {},
): CustomNode => {
  const newNodeId = options.nodeId ?? uuidv4();

  // Generate auto-name if applicable (exclude artifact nodes which lack struct)
  const autoName = generateAutoNodeName(
    nodeStruct,
    existingNodes.filter((n) => !isArtifactNode(n)),
  );

  // Apply auto-name and default values to params
  const updatedParams = nodeStruct.params.map((param) => {
    // 1. Set name parameters
    if (
      isNameParameter(param, nodeStruct) &&
      (param.value === '' || param.value === undefined || param.value === null)
    ) {
      // For AGENT nodes, use auto-generated name with number (e.g., "Agent 1")
      // For targeted governance nodes, just use the display_name without number
      const nameValue =
        nodeStruct.node_type === NodeCategory.AGENT
          ? autoName
          : nodeStruct.display_name;
      if (nameValue) {
        return { ...param, value: nameValue };
      }
    }

    // 2. Apply default values to empty fields
    if (
      (param.value === undefined ||
        param.value === null ||
        param.value === '') &&
      param.default !== undefined &&
      param.default !== null &&
      param.default !== '' &&
      (param.required ||
        param.advanced ||
        typeof param.default === 'number' ||
        typeof param.default === 'boolean' ||
        (typeof param.default === 'string' && param.default.trim() !== ''))
    ) {
      return { ...param, value: param.default };
    }

    return param;
  });

  // For AGENT nodes, set user_config_name to auto-generated name (shows in node title)
  // For targeted governance nodes (Evaluations, Text Redaction, PDF Redaction),
  // only pre-fill the input field - don't change the node title
  const shouldSetUserConfigName =
    autoName && nodeStruct.node_type === NodeCategory.AGENT;

  return {
    id: newNodeId,
    type: NodeType.GenericNode,
    position,
    data: {
      struct: {
        ...nodeStruct,
        react_flow_generated_id: newNodeId,
        user_config_name: shouldSetUserConfigName
          ? autoName
          : nodeStruct.user_config_name,
        params: updatedParams,
      },
    },
    selected: options.selected ?? false,
  };
};

// =============================================================================
// Connection Utilities - Shared helpers for managing incoming_connections
// =============================================================================

type IncomingConnection = {
  node_id: string; // source
  port_id: string; // sourceHandle
};

// Empty placeholder connection used when no connections exist
export const EMPTY_CONNECTION: IncomingConnection[] = [
  { node_id: '', port_id: '' },
];

/**
 * Check if a connection is non-empty (has actual node_id and port_id)
 */
const isNonEmptyConnection = (conn: IncomingConnection): boolean =>
  conn.node_id !== '' && conn.port_id !== '';

/**
 * Check if connections array has any non-empty connections
 */
const hasNonEmptyConnections = (connections: IncomingConnection[]): boolean =>
  connections.some(isNonEmptyConnection);

/**
 * Add a new connection to an array, handling duplicates and empty placeholders
 * - Removes empty placeholder connections before adding
 * - Prevents duplicate connections
 * - Returns updated array
 */
const addIncomingConnection = (
  existingConnections: IncomingConnection[] | undefined,
  newConnection: IncomingConnection,
): IncomingConnection[] => {
  const connections = existingConnections || [];

  // Check if connection already exists
  const connectionExists = connections.some(
    (conn) =>
      conn.node_id === newConnection.node_id &&
      conn.port_id === newConnection.port_id,
  );

  if (connectionExists) {
    return connections;
  }

  // Filter out empty placeholders and add new connection
  const validConnections = connections.filter(isNonEmptyConnection);
  return [...validConnections, newConnection];
};

/**
 * Remove connections that match the deleted edges
 * - Returns array with matching connections removed
 * - Adds empty placeholder if all connections are removed
 */
const removeIncomingConnections = (
  existingConnections: IncomingConnection[] | undefined,
  edgesToDelete: CustomEdge[],
): IncomingConnection[] => {
  const connections = existingConnections || [];

  const updatedConnections = connections.filter(
    (conn) =>
      !edgesToDelete.some(
        (edge) =>
          edge.source === conn.node_id && edge.sourceHandle === conn.port_id,
      ),
  );

  // Add empty placeholder if no connections remain
  if (updatedConnections.length === 0) {
    return EMPTY_CONNECTION;
  }

  return updatedConnections;
};

// =============================================================================

interface RecentlyUsedNode {
  name: string;
  nodeStruct: NodeStruct;
  icon: React.ReactNode;
  lastUsed: number;
  category: string;
}

// Tracks if the user is dragging a connection
type ActiveConnect = {
  side: 'source' | 'target';
  nodeId: string;
  handleId: string;
} | null;

// History snapshot for undo/redo
export interface HistorySnapshot {
  nodes: CustomNode[];
  edges: CustomEdge[];
}

/** Derived selection from nodes[].selected. Use with useShallow to avoid unnecessary re-renders. */
export const selectSelectedNodeIds = (state: {
  nodes: CustomNode[];
}): string[] => state.nodes.filter((n) => n.selected).map((n) => n.id);

interface NodeStoreState {
  nodes: CustomNode[];
  edges: CustomEdge[];
  nodeResults: Record<string, unknown>;
  loadingNodes: Set<string>;
  cancellableNodes: Set<string>;
  isPipelineRunning: boolean;
  lastSelectedNodeId: string | null;
  /** Group whose padding/chrome is explicitly focused for dragging. */
  groupContainerFocusedId: string | null;
  copiedNodes: CustomNode[];
  copiedEdges: CustomEdge[];
  activeConnect: ActiveConnect;
  setActiveConnect: (ac: ActiveConnect) => void;
  clearActiveConnect: () => void;
  // Undo/Redo state
  history: HistorySnapshot[];
  currentHistoryIndex: number;
  // Node list state
  groupedNodes: GroupedNodes;
  isLoadingNodeList: boolean;
  // Node caching state
  cachedGroupedNodes: GroupedNodes;
  lastNodeFetchTime: number | null;
  isNodeCacheValid: boolean;
  // Recently used nodes for context menu
  recentlyUsedNodes: RecentlyUsedNode[];
  // Note: this boolean is used to conditionally disable reactFlow scroll behavior when textarea is hovered
  isTextareaHovered: boolean;
  setIsTextareaHovered: (hovered: boolean) => void;
  // Undo/Redo actions
  saveSnapshot: (providedSnapshot?: HistorySnapshot) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;
  // Unified selection actions (single source of truth: nodes[].selected)
  selectNode: (nodeId: string) => void;
  reduceSelectionToNode: (nodeId: string) => void;
  deselectNode: (nodeId: string) => void;
  deselectAllNodes: () => void;
  // Actions
  setNodes: (nodes: CustomNode[]) => void;
  setEdges: (edges: CustomEdge[]) => void;
  setLastSelectedNodeId: (id: string | null) => void;
  setFocusedGroupId: (id: string | null) => void;
  setGroupContainerFocusedId: (id: string | null) => void;
  setCopiedNodes: (nodes: CustomNode[]) => void;
  setCopiedEdges: (edges: CustomEdge[]) => void;
  setNodeResults: (results: Record<string, InputValue>) => void;
  updateNodeResult: (nodeId: string, result: unknown) => void;
  setLoadingNodes: (ids: Set<string>) => void;
  setCancellableNodes: (ids: Set<string>) => void;
  setIsPipelineRunning: (running: boolean) => void;
  // Node list actions
  fetchNodes: (forceRefresh?: boolean) => Promise<void>;
  setGroupedNodes: (nodes: GroupedNodes) => void;
  setIsLoadingNodeList: (loading: boolean) => void;
  // Cache actions
  setCachedGroupedNodes: (nodes: GroupedNodes) => void;
  setLastNodeFetchTime: (timestamp: number) => void;
  setIsNodeCacheValid: (valid: boolean) => void;
  clearNodeCache: () => void;
  // Recently used nodes actions
  addToRecentlyUsed: (node: RecentlyUsedNode) => void;
  clearRecentlyUsed: () => void;
  // Node/edge manipulation
  onCopyNode: (nodeId?: string) => void;
  onPasteNode: (options?: {
    offset?: { x: number; y: number };
    viewportCenter?: { x: number; y: number };
    viewportBounds?: { x: number; y: number; w: number; h: number };
  }) => void;
  onDuplicateNode: (nodeId?: string) => void;
  // Unified delete handler for nodes and edges
  // - When called from ReactFlow's onDelete: pass { nodes, edges } directly
  // - When called manually with nodeId: pass { nodeId } to delete single node and its edges
  // - When called manually without params: deletes selected nodes and their edges
  onNodesEdgesDelete: (params?: {
    nodes?: CustomNode[];
    edges?: CustomEdge[];
    nodeId?: string;
  }) => void;
  handleParameterChange: (
    nodeId: string,
    fieldId: string,
    value: InputValue,
    key?: string,
  ) => void;
  updateNodeAttributes: (
    nodeId: string,
    updates: {
      user_config_name?: string;
      user_config_description?: string;
      icon?: string;
      icon_color?: string;
    },
  ) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onEdgesDelete: (edgesToDelete: CustomEdge[]) => void;
  onConnect: (params: Connection) => void;
  onReconnect: (
    oldEdge: CustomEdge,
    newConnection: Connection,
    edgeReconnectSuccessful: React.RefObject<boolean>,
  ) => void;
  onReconnectStart: (edgeReconnectSuccessful: React.RefObject<boolean>) => void;
  onReconnectEnd: (
    edgeReconnectSuccessful: React.RefObject<boolean>,
    edge: CustomEdge,
  ) => void;
  addNode: (
    struct: NodeStruct,
    position?: { x: number; y: number },
    options?: {
      selected?: boolean;
      nodeId?: string;
      setCenter?: SetCenter;
      zoom?: number;
      rightSidebarWidthPx?: number;
    },
  ) => string;
  /** Adds multiple nodes in a single atomic state update. Returns the IDs of all created nodes. */
  addNodes: (
    entries: { struct: NodeStruct; position: { x: number; y: number } }[],
  ) => string[];
  /**
   * Sticky-note canvas annotations.
   *
   * - `pendingStickyNoteColor` is the color the user picked in the toolbar's
   *   color bar; consumed by `onPaneClick` when dropping a sticky note.
   * - `addStickyNote` creates a sticky-note node at the given canvas position
   *   WITHOUT calling `selectNode()` so the console panel does NOT open.
   *   The node is still marked `selected: true` so its floating toolbar /
   *   resize handles show up.
   * - `updateStickyNoteData` merges presentation fields into a sticky note's
   *   `data` (text, backgroundColor). Pushes an undo snapshot only on color
   *   changes (text edits would otherwise create a snapshot on every keystroke).
   */
  pendingStickyNoteColor: StickyNoteColor;
  setPendingStickyNoteColor: (color: StickyNoteColor) => void;
  addStickyNote: (
    position: { x: number; y: number },
    options?: { backgroundColor?: StickyNoteColor; text?: string },
  ) => string;
  updateStickyNoteData: (
    nodeId: string,
    patch: Partial<Pick<StickyNoteData, 'text' | 'backgroundColor'>>,
  ) => void;
  addAgentHandlePortConn: (
    nodes: CustomNode[],
    params: Connection,
    targetNode: CustomNode,
  ) => CustomNode[];
  addParamPortConn: (
    nodes: CustomNode[],
    targetNodeId: string,
    targetHandleId: string,
    sourceNodeId: string,
    sourceHandleId: string,
  ) => CustomNode[];
  dragOverNodeId: string | null;
  setDragOverNodeId: (nodeId: string | null) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (
    event: React.DragEvent<HTMLDivElement>,
    dndPayload: DnDPayload,
    screenToFlowPosition: (pos: { x: number; y: number }) => {
      x: number;
      y: number;
    },
    dropTargetNodeId?: string,
  ) => string | undefined;
  validateConnection: (
    sourceNode: CustomNode,
    targetNode: CustomNode,
    sourceHandle: string | null,
    targetHandle: string | null,
  ) => { isValid: boolean; error?: string };
  isValidEdgeConnection: (connection: Connection) => boolean;
  getCompatibleNodes: (nodeStruct: NodeStruct) => NodeStruct[];
  getCompatibleSourceNodes: (
    nodeStruct: NodeStruct,
    inputPortId?: string,
  ) => NodeStruct[];
  /**
   * Creates a SelectNode at `position` connected to the given handle, using
   * FRESH nodes/edges read via get() so a stale closure can never overwrite
   * in-flight edits. Replaces any existing SelectNode on the same handle.
   * Returns the new SelectNode's React Flow id.
   */
  createSelectNodeForConnection: (params: {
    fromNodeId: string;
    fromHandleId: string | null;
    fromHandleType: 'source' | 'target';
    position: { x: number; y: number };
    compatibleNodes: NodeStruct[];
    acceptedTypes: string[];
  }) => string;
  updateNodeToCurrentVersion: (
    nodeReactFlowId: string,
    groupedNodes: GroupedNodes,
  ) => void;
  updateAvailableNodes: NodeUpdateAvailable[];
  detectNodeUpdates: () => void;
  removeNodeUpdateAvailable: (nodeId: string) => void;
}

export const useNodeStore = create<NodeStoreState>()(
  immer((set, get) => {
    // Load clipboard from localStorage on initialization
    const savedClipboard = loadClipboardFromStorage();

    return {
      nodes: [] as CustomNode[],
      edges: [] as CustomEdge[],
      nodeResults: {},
      loadingNodes: new Set(),
      cancellableNodes: new Set(),
      isPipelineRunning: false,
      lastSelectedNodeId: null,
      groupContainerFocusedId: null,
      copiedNodes: savedClipboard.nodes,
      copiedEdges: savedClipboard.edges,
      activeConnect: null,
      setActiveConnect: (ac) => set({ activeConnect: ac }),
      clearActiveConnect: () => set({ activeConnect: null }),
      // Undo/Redo state
      history: [],
      currentHistoryIndex: -1,
      // Node list state
      groupedNodes: {} as GroupedNodes,
      isLoadingNodeList: false,
      // Node caching state
      cachedGroupedNodes: {} as GroupedNodes,
      lastNodeFetchTime: null,
      isNodeCacheValid: false,
      // Recently used nodes state
      recentlyUsedNodes: [],
      isTextareaHovered: false,
      setIsTextareaHovered: (hovered) => set({ isTextareaHovered: hovered }),
      updateAvailableNodes: [] as NodeUpdateAvailable[],
      // Selected color for the next sticky-note placement (driven by the
      // color bar that pops above the mouse-control-bar).
      pendingStickyNoteColor: DEFAULT_STICKY_NOTE_COLOR,
      setPendingStickyNoteColor: (color) =>
        set({ pendingStickyNoteColor: color }),
      // Undo/Redo actions
      saveSnapshot: (providedSnapshot?: HistorySnapshot) => {
        if (snapshotSuppressionDepth > 0) return;

        const { nodes, edges, history, currentHistoryIndex } = get();
        const snapshot: HistorySnapshot = providedSnapshot || {
          nodes: structuredClone(nodes),
          edges: structuredClone(edges),
        };

        // Check if the new snapshot is different from the current one
        if (currentHistoryIndex >= 0) {
          const currentSnapshot = history[currentHistoryIndex];
          const nodesEqual =
            JSON.stringify(currentSnapshot.nodes) ===
            JSON.stringify(snapshot.nodes);
          const edgesEqual =
            JSON.stringify(currentSnapshot.edges) ===
            JSON.stringify(snapshot.edges);

          // Don't save if snapshot is identical to current state
          if (nodesEqual && edgesEqual) {
            return;
          }
        }

        // Remove any future history if we're not at the end
        const newHistory = history.slice(0, currentHistoryIndex + 1);

        // Add new snapshot
        newHistory.push(snapshot);

        set({
          history: newHistory,
          currentHistoryIndex: newHistory.length - 1,
        });
      },
      undo: () => {
        const { history, currentHistoryIndex, groupContainerFocusedId } = get();

        if (currentHistoryIndex > 0) {
          const previousSnapshot = history[currentHistoryIndex - 1];
          set({
            // Snapshots bake in whatever group focus/interaction state was
            // live when they were captured — re-sync against the current
            // focus id rather than restoring a stale draggable/className.
            nodes: syncGroupNodeInteractionState(
              structuredClone(previousSnapshot.nodes),
              groupContainerFocusedId,
            ),
            edges: structuredClone(previousSnapshot.edges),
            currentHistoryIndex: currentHistoryIndex - 1,
          });
        }
      },
      redo: () => {
        const { history, currentHistoryIndex, groupContainerFocusedId } = get();

        if (currentHistoryIndex < history.length - 1) {
          const nextSnapshot = history[currentHistoryIndex + 1];
          set({
            nodes: syncGroupNodeInteractionState(
              structuredClone(nextSnapshot.nodes),
              groupContainerFocusedId,
            ),
            edges: structuredClone(nextSnapshot.edges),
            currentHistoryIndex: currentHistoryIndex + 1,
          });
        }
      },
      canUndo: () => {
        const { currentHistoryIndex } = get();
        return currentHistoryIndex > 0;
      },
      canRedo: () => {
        const { history, currentHistoryIndex } = get();
        return currentHistoryIndex < history.length - 1;
      },
      clearHistory: () => {
        set({
          history: [],
          currentHistoryIndex: -1,
        });
      },

      // Unified selection: selects a single node, updates nodes[].selected and lastSelectedNodeId, opens console
      selectNode: (nodeId) => {
        const { nodes } = get();
        const updatedNodes = nodes.map((n) => ({
          ...n,
          selected: n.id === nodeId,
        }));
        set({
          nodes: syncGroupNodeInteractionState(updatedNodes, null),
          lastSelectedNodeId: nodeId,
          groupContainerFocusedId: null,
        });
        useWorkflowPanelStore.getState().setConsolePanelOpen(true);
      },
      // Narrows an existing multi-selection down to a single node. Same
      // selection transform as selectNode, minus the console panel side
      // effect — callers on the click path (WorkflowEditorPage's onNodeClick)
      // decide the panel state themselves, and sticky notes explicitly close
      // it, so forcing it open here would fight them.
      reduceSelectionToNode: (nodeId) => {
        const { nodes } = get();
        const updatedNodes = nodes.map((n) => ({
          ...n,
          selected: n.id === nodeId,
        }));
        set({
          nodes: syncGroupNodeInteractionState(updatedNodes, null),
          lastSelectedNodeId: nodeId,
          groupContainerFocusedId: null,
        });
      },
      // Drops one node from a selection, leaving the rest. Backs the close
      // button on a stacked inspector card, which speaks for its own node.
      deselectNode: (nodeId) => {
        const { nodes, edges, lastSelectedNodeId } = get();
        const updatedNodes = nodes.map((n) =>
          n.id === nodeId ? { ...n, selected: false } : n,
        );
        const nextSelected = updatedNodes.find((n) => n.selected);
        // A band selection selects the edges between the nodes too, and an
        // edge only shows its Delete toolbar once no node is selected — so
        // drop the edges with the node, or they surface as the last card goes.
        const updatedEdges = edges.map((edge) =>
          edge.selected &&
          (!nextSelected || edge.source === nodeId || edge.target === nodeId)
            ? { ...edge, selected: false }
            : edge,
        );
        set({
          nodes: syncGroupNodeInteractionState(updatedNodes, null),
          edges: updatedEdges,
          lastSelectedNodeId:
            lastSelectedNodeId === nodeId
              ? (nextSelected?.id ?? null)
              : lastSelectedNodeId,
        });
      },
      // Unified deselection: deselects all nodes, clears lastSelectedNodeId, closes console
      deselectAllNodes: () => {
        const { nodes } = get();
        const updatedNodes = nodes.map((n) => ({
          ...n,
          selected: false,
        }));
        set({
          nodes: syncGroupNodeInteractionState(updatedNodes, null),
          lastSelectedNodeId: null,
          groupContainerFocusedId: null,
        });
        useWorkflowPanelStore.getState().setConsolePanelOpen(false);
      },
      setNodes: (nodes) => {
        const { groupContainerFocusedId, nodes: currentNodes } = get();
        const synced = syncGroupNodeInteractionState(
          nodes,
          groupContainerFocusedId,
        );
        if (synced !== currentNodes) {
          set({ nodes: synced });
        }
      },
      setEdges: (edges) => set({ edges }),
      setLastSelectedNodeId: (id) => set({ lastSelectedNodeId: id }),
      setGroupContainerFocusedId: (id) => {
        const { groupContainerFocusedId, nodes } = get();
        if (groupContainerFocusedId === id) return;
        const synced = syncGroupNodeInteractionState(nodes, id);
        if (synced !== nodes) {
          set({ groupContainerFocusedId: id, nodes: synced });
        } else {
          set({ groupContainerFocusedId: id });
        }
      },
      /** @deprecated Use setGroupContainerFocusedId */
      setFocusedGroupId: (id) => get().setGroupContainerFocusedId(id),
      setCopiedNodes: (nodes) => set({ copiedNodes: nodes }),
      setCopiedEdges: (edges) => set({ copiedEdges: edges }),
      setNodeResults: (results) => set({ nodeResults: results }),
      updateNodeResult: (nodeId, result) =>
        set((state) => {
          state.nodes.forEach((node) => {
            if (node.id === nodeId) {
              (node.data as NodeData).result = result as NodeResult;
            }
          });
        }),
      setLoadingNodes: (ids) => set({ loadingNodes: ids }),
      setCancellableNodes: (ids) => set({ cancellableNodes: ids }),
      setIsPipelineRunning: (running) => set({ isPipelineRunning: running }),
      // Node list actions
      setGroupedNodes: (nodes) => set({ groupedNodes: nodes }),
      setIsLoadingNodeList: (loading) => set({ isLoadingNodeList: loading }),
      // Cache actions
      setCachedGroupedNodes: (nodes) => set({ cachedGroupedNodes: nodes }),
      setLastNodeFetchTime: (timestamp) =>
        set({ lastNodeFetchTime: timestamp }),
      setIsNodeCacheValid: (valid) => set({ isNodeCacheValid: valid }),
      clearNodeCache: () =>
        set({
          groupedNodes: {} as GroupedNodes,
          cachedGroupedNodes: {} as GroupedNodes,
          lastNodeFetchTime: null,
          isNodeCacheValid: false,
        }),
      // Recently used nodes actions
      addToRecentlyUsed: (node) =>
        set((state) => {
          // Remove existing entry if it exists
          const filtered = state.recentlyUsedNodes.filter(
            (n) => n.name !== node.name,
          );
          // Add new entry and limit to 10 items
          return { recentlyUsedNodes: [node, ...filtered].slice(0, 10) };
        }),
      clearRecentlyUsed: () => set({ recentlyUsedNodes: [] }),
      fetchNodes: async (forceRefresh = false) => {
        const { cachedGroupedNodes, lastNodeFetchTime, isNodeCacheValid } =
          get();
        const now = Date.now();
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

        // Get organization ID
        const orgId = useOrgStore.getState().currentOrg?.orgid;
        if (!orgId) {
          console.warn('No organization ID available, cannot fetch nodes');
          // If we have cached data, use it
          if (Object.keys(cachedGroupedNodes).length > 0) {
            set({
              groupedNodes: cachedGroupedNodes,
              isLoadingNodeList: false,
            });
          } else {
            set({ isLoadingNodeList: false });
          }
          return;
        }

        // Check if we have valid cached data and it's not expired
        const isCacheValid =
          isNodeCacheValid &&
          lastNodeFetchTime &&
          now - lastNodeFetchTime < CACHE_DURATION &&
          Object.keys(cachedGroupedNodes).length > 0;

        // If we have valid cached data and not forcing refresh, use cache immediately
        if (isCacheValid && !forceRefresh) {
          set({
            groupedNodes: cachedGroupedNodes,
            isLoadingNodeList: false,
          });

          // Still fetch in background to update cache for next time
          setTimeout(() => {
            get().fetchNodes(true);
          }, 100);
          return;
        }

        // If we have expired cache but not forcing refresh, show it immediately
        if (Object.keys(cachedGroupedNodes).length > 0 && !forceRefresh) {
          set({
            groupedNodes: cachedGroupedNodes,
            isLoadingNodeList: false,
          });
        }

        try {
          set({ isLoadingNodeList: true });

          const list = await getNodeList(orgId);

          const groupedNodes: GroupedNodes = {};
          // Group nodes by node_type
          list?.data?.catalog?.forEach((nodeData: NodeStruct) => {
            const category = nodeData.node_type || 'Unknown';

            if (!groupedNodes[category]) {
              groupedNodes[category] = [];
            }

            groupedNodes[category].push({
              name: nodeData.display_name,
              ...nodeData,
            } as ApiNodeData);
          });

          // Sort nodes within each category alphabetically by display_name
          Object.keys(groupedNodes).forEach((category) => {
            groupedNodes[category].sort((a, b) => {
              const nameA = a.display_name;
              const nameB = b.display_name;
              return nameA.localeCompare(nameB);
            });
          });

          // Update both current state and cache
          set({
            groupedNodes: groupedNodes,
            cachedGroupedNodes: groupedNodes,
            lastNodeFetchTime: now,
            isNodeCacheValid: true,
          });
        } catch (error) {
          console.error('Failed to fetch nodes:', error);
          Sentry.captureException(error, {
            extra: { context: 'get-node-list-error' },
          });
          // If we have cached data and fetch failed, keep using cache
          if (Object.keys(cachedGroupedNodes).length > 0) {
            set({
              groupedNodes: cachedGroupedNodes,
              isLoadingNodeList: false,
            });
          }
        } finally {
          set({ isLoadingNodeList: false });
        }
      },
      onCopyNode: (nodeId?: string) => {
        const { nodes, edges, setCopiedNodes, setCopiedEdges } = get();
        let nodesToCopy = (
          nodeId
            ? nodes.filter((node) => node.id === nodeId)
            : nodes.filter((node) => node.selected)
        ).filter(
          (node) =>
            node.type !== NodeType.ArtifactNode &&
            node.type !== NodeType.GroupNode,
        );
        if (nodesToCopy.length === 0) return;

        const copiedNodeIds = new Set(nodesToCopy.map((node) => node.id));
        const nodeById = new Map(nodes.map((node) => [node.id, node]));

        // Flatten positions for grouped nodes whose parent is not being copied
        nodesToCopy = nodesToCopy.map((node) => {
          if (!node.parentId || copiedNodeIds.has(node.parentId)) {
            return node;
          }

          return {
            ...node,
            position: getAbsolutePosition(node, nodeById),
            parentId: undefined,
          };
        });

        // Copy edges that connect between the copied nodes (both source and target must be in copied nodes)
        const edgesToCopy = edges.filter(
          (edge: CustomEdge) =>
            copiedNodeIds.has(edge.source) && copiedNodeIds.has(edge.target),
        );

        setCopiedNodes(nodesToCopy);
        setCopiedEdges(edgesToCopy);

        // Save clipboard data to localStorage (allow cross-tab copy pasting)
        saveClipboardToStorage(nodesToCopy, edgesToCopy);
      },
      onPasteNode: (options) => {
        const {
          setNodes,
          setEdges,
          nodes,
          edges,
          saveSnapshot,
          copiedNodes: copiedNodesFromZustand,
          copiedEdges: copiedEdgesFromZustand,
        } = get();
        const offset = options?.offset ??
          options?.viewportCenter ?? { x: 50, y: 50 };

        // Load clipboard data from localStorage, default to zustand state if localStorage is not available
        const clipboardData = loadClipboardFromStorage();
        const copiedNodes = clipboardData.nodes || copiedNodesFromZustand;
        const copiedEdges = clipboardData.edges || copiedEdgesFromZustand;

        if (copiedNodes.length === 0) return;

        // Deselect all existing nodes and edges first
        const deselectedNodes = nodes.map((node) => ({
          ...node,
          selected: false,
        }));

        const deselectedEdges = edges.map((edge) => ({
          ...edge,
          selected: false,
        }));

        const NUDGE_STEP = 30;
        const MAX_NUDGE_STEPS = 100;

        const CELL_SIZE = 50;
        const occupiedCells = new Set<string>();
        // Grouped children store position relative to their parent group,
        // not absolute canvas coordinates — resolve absolute position so
        // overlap detection isn't fooled by mixing the two coordinate spaces.
        const nodeById = new Map(nodes.map((node) => [node.id, node]));
        for (const n of nodes) {
          const w = n.measured?.width ?? DEFAULT_NODE_WIDTH;
          const h = n.measured?.height ?? DEFAULT_NODE_HEIGHT;
          const absPos = getAbsolutePosition(n, nodeById);
          const minCol = Math.floor(absPos.x / CELL_SIZE);
          const maxCol = Math.floor((absPos.x + w) / CELL_SIZE);
          const minRow = Math.floor(absPos.y / CELL_SIZE);
          const maxRow = Math.floor((absPos.y + h) / CELL_SIZE);
          for (let col = minCol; col <= maxCol; col++) {
            for (let row = minRow; row <= maxRow; row++) {
              occupiedCells.add(`${col},${row}`);
            }
          }
        }

        const firstNodePos = copiedNodes[0]?.position ?? { x: 0, y: 0 };
        const hasAnyOverlapAt = (ox: number, oy: number) =>
          copiedNodes.some((cn) => {
            const fx = ox + (cn.position.x - firstNodePos.x);
            const fy = oy + (cn.position.y - firstNodePos.y);
            const cw = cn.measured?.width ?? DEFAULT_NODE_WIDTH;
            const ch = cn.measured?.height ?? DEFAULT_NODE_HEIGHT;
            const minCol = Math.floor(fx / CELL_SIZE);
            const maxCol = Math.floor((fx + cw) / CELL_SIZE);
            const minRow = Math.floor(fy / CELL_SIZE);
            const maxRow = Math.floor((fy + ch) / CELL_SIZE);
            for (let col = minCol; col <= maxCol; col++) {
              for (let row = minRow; row <= maxRow; row++) {
                if (occupiedCells.has(`${col},${row}`)) return true;
              }
            }
            return false;
          });

        let nudgedOffset = { x: offset.x, y: offset.y };
        if (hasAnyOverlapAt(nudgedOffset.x, nudgedOffset.y)) {
          const vp = options?.viewportBounds;
          let found = false;

          if (vp) {
            const cx = offset.x;
            const cy = offset.y;

            const tryPosition = (tx: number, ty: number) => {
              if (found) return;
              if (
                tx < vp.x ||
                ty < vp.y ||
                tx > vp.x + vp.w ||
                ty > vp.y + vp.h
              )
                return;
              if (!hasAnyOverlapAt(tx, ty)) {
                nudgedOffset = { x: tx, y: ty };
                found = true;
              }
            };

            tryPosition(cx, cy);

            for (let ring = 1; ring <= MAX_NUDGE_STEPS && !found; ring++) {
              const step = ring * NUDGE_STEP;
              for (let d = -step; d <= step && !found; d += NUDGE_STEP) {
                tryPosition(cx + d, cy - step);
                tryPosition(cx + step, cy + d);
                tryPosition(cx + d, cy + step);
                tryPosition(cx - step, cy + d);
              }
            }
          }

          if (!found) {
            for (let i = 0; i < MAX_NUDGE_STEPS; i++) {
              nudgedOffset = {
                x: nudgedOffset.x + NUDGE_STEP,
                y: nudgedOffset.y,
              };
              if (!hasAnyOverlapAt(nudgedOffset.x, nudgedOffset.y)) break;
            }
          }
        }

        const { nodes: newNodes, edges: newEdges } = remapNodesAndEdges(
          copiedNodes,
          copiedEdges,
          { offset: nudgedOffset, selected: true },
        );

        setNodes([...deselectedNodes, ...newNodes]);
        setEdges([...deselectedEdges, ...newEdges]);

        // Save snapshot after pasting
        saveSnapshot();
      },
      onDuplicateNode: (nodeId?: string) => {
        const { onCopyNode, onPasteNode } = get();
        onCopyNode(nodeId);

        // Get the updated copiedNodes after onCopyNode
        const { copiedNodes } = get();

        if (copiedNodes.length > 0) {
          // Calculate offset to place near original node
          const firstNode = copiedNodes[0];
          onPasteNode({
            offset: {
              x: firstNode.position.x + 50,
              y: firstNode.position.y + 50,
            },
          });
        } else {
          onPasteNode();
        }
      },
      onNodesEdgesDelete: (params?: {
        nodes?: CustomNode[];
        edges?: CustomEdge[];
        nodeId?: string;
      }) => {
        const {
          setNodes,
          setEdges,
          nodes,
          edges,
          saveSnapshot,
          groupContainerFocusedId,
        } = get();

        const selectedIds = new Set(
          nodes.filter((n) => n.selected).map((n) => n.id),
        );

        let nodesToDelete: CustomNode[];
        let edgesToDelete: CustomEdge[];

        if (params?.nodes && params?.edges) {
          // Called from ReactFlow's onDelete - nodes and edges provided directly
          nodesToDelete = params.nodes;
          edgesToDelete = params.edges as CustomEdge[];
        } else if (params?.nodeId) {
          // Single node deletion by ID (manual trigger)
          const nodeId = params.nodeId;
          nodesToDelete = nodes.filter((node) => node.id === nodeId);
          edgesToDelete = edges.filter(
            (edge: CustomEdge) =>
              edge.source === nodeId || edge.target === nodeId,
          );
        } else {
          // Delete selected nodes (manual trigger - cut shortcut, context menu)
          nodesToDelete = nodes.filter((node) => node.selected);
          edgesToDelete = edges.filter(
            (edge: CustomEdge) =>
              selectedIds.has(edge.source) || selectedIds.has(edge.target),
          );
        }

        // Artifact nodes/edges can only be removed via cascade when parent is deleted
        nodesToDelete = nodesToDelete.filter(
          (n) => n.type !== NodeType.ArtifactNode,
        );
        edgesToDelete = edgesToDelete.filter((e) => !isArtifactEdge(e));

        if (nodesToDelete.length === 0 && edgesToDelete.length === 0) {
          return; // Nothing to delete
        }

        // Get the IDs of nodes being deleted, including artifact children
        const nodeIdsToDelete = new Set(nodesToDelete.map((n) => n.id));

        // Cascade: if a parent node is deleted, also delete its artifact children
        nodes.forEach((n) => {
          if (
            n.type === NodeType.ArtifactNode &&
            (n.data as Record<string, unknown>)?.parentNodeId &&
            nodeIdsToDelete.has(
              (n.data as Record<string, unknown>).parentNodeId as string,
            )
          ) {
            nodeIdsToDelete.add(n.id);
          }
        });

        // Include edges connected to cascade-deleted artifact nodes
        const edgeIds = new Set(edgesToDelete.map((e) => e.id));
        edges.forEach((e) => {
          if (
            !edgeIds.has(e.id) &&
            (nodeIdsToDelete.has(e.source) || nodeIdsToDelete.has(e.target))
          ) {
            edgesToDelete.push(e);
          }
        });

        // Clear all validation errors tied to deleted nodes
        if (nodeIdsToDelete.size > 0) {
          const validationStore = useWorkflowValidationStore.getState();
          validationStore.missingFiles
            .filter((e) => nodeIdsToDelete.has(e.nodeId))
            .forEach((e) => validationStore.removeMissingFile(e.id));
          validationStore.fieldErrors
            .filter((e) => nodeIdsToDelete.has(e.nodeId))
            .forEach((e) => validationStore.removeFieldError(e.id));
          validationStore.unsupportedNodes
            .filter((e) => nodeIdsToDelete.has(e.nodeId))
            .forEach((e) => validationStore.removeUnsupportedNode(e.id));
        }

        // Promote grouped children to absolute positions before the deleted
        // group node itself is filtered out — promoteGroupChildrenOnDelete
        // needs the group still present in `nodes` to detect it's a group.
        const nodesToKeep = promoteGroupChildrenOnDelete(
          nodes,
          nodeIdsToDelete,
        ).filter((node: CustomNode) => !nodeIdsToDelete.has(node.id));

        // Update the incoming connections for nodes that remain
        const updatedNodes = expandGroupNodes(
          updatePortOnEdgeDelete(edgesToDelete, nodesToKeep),
        );

        // Remove the edges
        setEdges(
          edges.filter(
            (edge: CustomEdge) =>
              !edgesToDelete.some((toDelete) => toDelete.id === edge.id),
          ),
        );

        // Remove the nodes and apply port updates in a single setNodes call
        setNodes(updatedNodes);

        // The focused group itself may have been deleted directly, or
        // collapsed to zero/one children by expandGroupNodes above — either
        // way, don't leave groupContainerFocusedId pointing at a node that
        // no longer exists.
        if (
          groupContainerFocusedId &&
          !updatedNodes.some((node) => node.id === groupContainerFocusedId)
        ) {
          set({ groupContainerFocusedId: null });
        }

        // Save snapshot after removing
        saveSnapshot();
      },
      handleParameterChange: (
        nodeId: string,
        fieldId: string,
        value: InputValue,
        key?: string,
      ) => {
        if (!key && !isBlankValue(value)) {
          useWorkflowValidationStore
            .getState()
            .clearFieldError(nodeId, fieldId);
        }
        set((state) => {
          state.nodes.forEach((node, index) => {
            if (node.id === nodeId && node.data.struct) {
              // Update the field value directly in struct.inputs[].value
              const updatedInputs = node.data.struct.params.map((param) => {
                if (param.field_id === fieldId) {
                  if (key) {
                    return { ...param, [key]: value };
                  } else {
                    return { ...param, value };
                  }
                }
                // Return a new object to ensure React re-renders
                return { ...param };
              });

              // Special handling: sync "name" parameter to user_config_name
              // This ensures the canvas display name updates when the "Agent Name"
              // field is changed in the configuration panel (bidirectional sync)
              const updatedStruct =
                fieldId === 'name' && typeof value === 'string' && !key
                  ? {
                      ...node.data.struct,
                      params: updatedInputs,
                      user_config_name: value,
                    }
                  : {
                      ...node.data.struct,
                      params: updatedInputs,
                    };

              state.nodes[index] = {
                ...node,
                data: {
                  ...node.data,
                  struct: updatedStruct,
                },
              };
            }
          });
        });
      },
      updateNodeAttributes: (
        nodeId: string,
        updates: {
          user_config_name?: string;
          user_config_description?: string;
          icon?: string;
          icon_color?: string;
        },
      ) => {
        set((state) => {
          state.nodes.forEach((node, index) => {
            if (node.id === nodeId && node.data.struct) {
              // Create a new struct with updates applied
              const newStruct = { ...node.data.struct };

              // Handle each field: if empty string, delete it; otherwise update it
              if (updates.user_config_name !== undefined) {
                if (updates.user_config_name === '') {
                  delete newStruct.user_config_name;
                } else {
                  newStruct.user_config_name = updates.user_config_name;
                }

                // Sync user_config_name to the "name" parameter for bidirectional sync
                // This ensures the config panel updates when the canvas name is changed
                newStruct.params = newStruct.params.map((param) => {
                  if (param.field_id === 'name') {
                    return { ...param, value: updates.user_config_name || '' };
                  }
                  return param;
                });
              }

              if (updates.user_config_description !== undefined) {
                if (updates.user_config_description === '') {
                  delete newStruct.user_config_description;
                } else {
                  newStruct.user_config_description =
                    updates.user_config_description;
                }
              }

              if (updates.icon !== undefined) {
                // Icon is a required field, so don't delete it
                // Just update it if a non-empty value is provided
                if (updates.icon !== '') {
                  newStruct.icon = updates.icon;
                }
              }

              if (updates.icon_color !== undefined) {
                if (updates.icon_color === '') {
                  delete newStruct.icon_color;
                } else {
                  newStruct.icon_color = updates.icon_color;
                }
              }

              state.nodes[index] = {
                ...node,
                data: {
                  ...node.data,
                  struct: newStruct,
                },
              };
            }
          });
        });
      },
      updateNodeToCurrentVersion: (
        nodeReactFlowId: string,
        groupedNodes: GroupedNodes,
      ) => {
        const state = get();
        const node = state.nodes.find((n) => n.id === nodeReactFlowId);
        if (!node?.data?.struct) return;

        const oldStruct = node.data.struct;

        const catalogStruct = Object.values(groupedNodes)
          .flat()
          .find((n) => n.node_id === oldStruct.node_id);
        if (!catalogStruct) return;

        // Preserve old port IDs by port name so edges and incoming_connections
        // on downstream nodes stay valid without any remapping.
        const oldOutputPortIdByName = new Map(
          oldStruct.output_ports.map((p) => [p.name, p.id]),
        );
        const oldInputPortIdByName = new Map(
          oldStruct.input_ports.map((p) => [p.name, p.id]),
        );
        const oldParamByFieldId = new Map(
          oldStruct.params.map((p) => [p.field_id, p]),
        );

        const newOutputPorts = catalogStruct.output_ports.map((p) => ({
          ...p,
          id: oldOutputPortIdByName.get(p.name) ?? p.id,
        }));

        const newInputPorts = catalogStruct.input_ports.map((p) => ({
          ...p,
          id: oldInputPortIdByName.get(p.name) ?? p.id,
        }));

        const newParams = catalogStruct.params.map((catalogParam) => {
          const oldParam = oldParamByFieldId.get(catalogParam.field_id);
          const oldValue = oldParam?.value;
          let param =
            oldValue !== undefined && oldValue !== null && oldValue !== ''
              ? { ...catalogParam, value: oldValue }
              : { ...catalogParam };

          // Preserve bindable_config port ID and connection state so the
          // incoming_connections on this param stay in sync with existing edges.
          if (
            param.bindable_config?.input_port &&
            oldParam?.bindable_config?.input_port
          ) {
            param = {
              ...param,
              bindable_config: {
                ...param.bindable_config,
                is_bound: oldParam.bindable_config.is_bound,
                input_port: {
                  ...param.bindable_config.input_port,
                  id: oldParam.bindable_config.input_port.id,
                  incoming_connections:
                    oldParam.bindable_config.input_port.incoming_connections,
                },
              },
            };
          }

          return param;
        });

        const newStruct: NodeStruct = {
          ...catalogStruct,
          react_flow_generated_id: oldStruct.react_flow_generated_id,
          user_config_name: oldStruct.user_config_name,
          user_config_description: oldStruct.user_config_description,
          icon_color: oldStruct.icon_color,
          output_ports: newOutputPorts,
          input_ports: newInputPorts,
          params: newParams,
        };

        // Edges don't need sourceHandle remapping since port IDs are preserved.
        // Only validate type compatibility and drop edges for removed ports.
        const newOutputPortIds = new Set(newOutputPorts.map((p) => p.id));
        const newInputPortIds = new Set([
          ...newInputPorts.map((p) => p.id),
          ...newParams
            .map((p) => p.bindable_config?.input_port?.id)
            .filter((id): id is string => !!id),
        ]);
        const removedEdgeIds = new Set<string>();

        const validatedEdges = state.edges.filter((edge) => {
          // Drop incoming edges whose target port was removed in the new version,
          // or whose source type is no longer compatible with the updated accepted_types.
          if (edge.target === nodeReactFlowId) {
            if (!newInputPortIds.has(edge.targetHandle ?? '')) {
              removedEdgeIds.add(edge.id);
              return false;
            }

            const newTargetPort = newInputPorts.find(
              (p) => p.id === edge.targetHandle,
            );
            const newTargetParamPort = newParams.find(
              (p) => p.bindable_config?.input_port?.id === edge.targetHandle,
            )?.bindable_config?.input_port;

            const targetAcceptedTypes =
              newTargetPort?.port_type.accepted_types ??
              newTargetParamPort?.port_type.accepted_types ??
              [];

            if (targetAcceptedTypes.length > 0) {
              const sourceNode = state.nodes.find((n) => n.id === edge.source);
              const sourceStruct = sourceNode?.data?.struct;
              const sourcePort = sourceStruct?.output_ports.find(
                (p) => p.id === edge.sourceHandle,
              );
              const sourceTypes = sourcePort?.port_type.accepted_types ?? [];

              if (!sourceTypes.some((t) => targetAcceptedTypes.includes(t))) {
                removedEdgeIds.add(edge.id);
                return false;
              }
            }

            return true;
          }

          if (edge.source !== nodeReactFlowId) return true;

          if (!newOutputPortIds.has(edge.sourceHandle ?? '')) {
            removedEdgeIds.add(edge.id);
            return false;
          }

          const newPort = newOutputPorts.find(
            (p) => p.id === edge.sourceHandle,
          );
          const targetNode = state.nodes.find((n) => n.id === edge.target);
          const targetStruct = targetNode?.data?.struct;
          if (!newPort || !targetStruct) {
            removedEdgeIds.add(edge.id);
            return false;
          }

          const targetPort = targetStruct.input_ports.find(
            (p) => p.id === edge.targetHandle,
          );
          const targetParamPort = targetStruct.params.find(
            (p) => p.bindable_config?.input_port?.id === edge.targetHandle,
          )?.bindable_config?.input_port;

          const targetTypes =
            targetPort?.port_type.accepted_types ??
            targetParamPort?.port_type.accepted_types ??
            [];
          const sourceTypes = newPort.port_type.accepted_types;

          if (
            targetTypes.length > 0 &&
            !sourceTypes.some((t) => targetTypes.includes(t))
          ) {
            removedEdgeIds.add(edge.id);
            return false;
          }

          return true;
        });

        const nodesWithNewStruct = state.nodes.map((n) =>
          n.id === nodeReactFlowId
            ? { ...n, data: { ...n.data, struct: newStruct } }
            : n,
        );

        const edgesToDelete = state.edges.filter((e) =>
          removedEdgeIds.has(e.id),
        );
        const updatedNodes = updatePortOnEdgeDelete(
          edgesToDelete,
          nodesWithNewStruct,
        );

        set({ nodes: updatedNodes, edges: validatedEdges });

        get().removeNodeUpdateAvailable(nodeReactFlowId);
      },

      removeNodeUpdateAvailable: (nodeId: string) =>
        set((state) => {
          state.updateAvailableNodes = state.updateAvailableNodes.filter(
            (n) => n.nodeId !== nodeId,
          );
        }),

      detectNodeUpdates: () => {
        const { nodes, edges, groupedNodes } = get();
        const catalogByNodeId = new Map<string, NodeStruct>();
        Object.values(groupedNodes)
          .flat()
          .forEach((n) => catalogByNodeId.set(n.node_id, n));

        const newNodes: NodeUpdateAvailable[] = [];
        nodes.forEach((node) => {
          const struct = node.data?.struct;
          if (!struct) return;
          if (
            node.type === NodeType.SelectNode ||
            node.type === NodeType.StickyNoteNode
          )
            return;

          const catalogStruct = catalogByNodeId.get(struct.node_id);
          if (!catalogStruct) return;

          if (isCatalogVersionNewer(struct.version, catalogStruct.version)) {
            newNodes.push({
              id: uuidv4(),
              nodeId: node.id,
              nodeName: struct.user_config_name || struct.display_name,
              nodeStructId: struct.node_id,
            });
            return;
          }

          const outgoingEdges = edges.filter((e) => e.source === node.id);
          for (const edge of outgoingEdges) {
            const storedPort = struct.output_ports.find(
              (p) => p.id === edge.sourceHandle,
            );
            if (!storedPort) continue;
            const catalogPort = catalogStruct.output_ports.find(
              (p) => p.name === storedPort.name,
            );
            if (!catalogPort) continue;
            const targetNode = nodes.find((n) => n.id === edge.target);
            if (!targetNode?.data?.struct) continue;
            const targetStruct = targetNode.data.struct;
            const targetPort = targetStruct.input_ports.find(
              (p) => p.id === edge.targetHandle,
            );
            const targetParamPort = targetStruct.params.find(
              (p) => p.bindable_config?.input_port?.id === edge.targetHandle,
            )?.bindable_config?.input_port;
            const targetTypes =
              targetPort?.port_type.accepted_types ??
              targetParamPort?.port_type.accepted_types ??
              [];
            const sourceTypes = catalogPort.port_type.accepted_types;
            if (
              targetTypes.length > 0 &&
              !sourceTypes.some((t) => targetTypes.includes(t))
            ) {
              newNodes.push({
                id: uuidv4(),
                nodeId: node.id,
                nodeName: struct.user_config_name || struct.display_name,
                nodeStructId: struct.node_id,
              });
              break;
            }
          }
        });

        set({ updateAvailableNodes: newNodes });
      },

      onNodesChange: (changes: NodeChange[]) => {
        const { nodes, saveSnapshot, groupContainerFocusedId } = get();

        // Create a deep copy of nodes to ensure they are mutable
        const mutableNodes = nodes.map((node) => {
          // Deep clone the entire node to ensure all properties are mutable
          const clonedNode = structuredClone(node);
          // Ensure the cloned node maintains the correct type
          return {
            ...clonedNode,
            type: node.type,
          } as CustomNode;
        });
        let updatedNodes = applyNodeChanges(
          changes,
          mutableNodes,
        ) as CustomNode[];

        updatedNodes = expandGroupNodes(updatedNodes);

        // Reposition artifact children when their parent node moves
        const positionEndChanges = changes.filter(
          (c) => c.type === 'position' && !('dragging' in c && c.dragging),
        );
        if (positionEndChanges.length > 0) {
          const artifactChildParentIds = new Set<string>();
          for (const n of updatedNodes) {
            if (n.type === NodeType.ArtifactNode) {
              const parentId = (n.data as Record<string, unknown>)
                ?.parentNodeId as string | undefined;
              if (parentId) artifactChildParentIds.add(parentId);
            }
          }

          for (const change of positionEndChanges) {
            const changedId = (change as { id: string }).id;
            if (artifactChildParentIds.has(changedId)) {
              const parentNode = updatedNodes.find((n) => n.id === changedId);
              if (parentNode) {
                updatedNodes = repositionArtifactNodes(
                  parentNode,
                  updatedNodes,
                );
              }
            }
          }
        }

        const draggingGroup = updatedNodes.find(
          (node) => isGroupNode(node) && node.dragging,
        );
        const effectiveContainerFocusId =
          draggingGroup?.id ?? groupContainerFocusedId;
        updatedNodes = syncGroupNodeInteractionState(
          updatedNodes,
          effectiveContainerFocusId,
        );

        const nextState: {
          nodes: CustomNode[];
          groupContainerFocusedId?: string | null;
        } = { nodes: updatedNodes };
        if (draggingGroup && draggingGroup.id !== groupContainerFocusedId) {
          nextState.groupContainerFocusedId = draggingGroup.id;
        }
        set(nextState);

        // check if the new node is a select node or artifact node, in which case we don't want to save a snapshot
        const node =
          changes.length > 0 && changes[0].type === 'dimensions'
            ? nodes.find(
                (node) => node.id === (changes[0] as NodeDimensionChange).id,
              )
            : undefined;
        const isRenderingSelectNode =
          changes[0]?.type === 'dimensions' &&
          (node?.type === NodeType.SelectNode ||
            node?.type === NodeType.GroupNode);

        // Skip snapshots when only artifact nodes changed
        const nonArtifactChanges = changes.filter((change) => {
          if (!('id' in change)) return true;
          const changedNode = nodes.find((n) => n.id === change.id);
          return changedNode?.type !== NodeType.ArtifactNode;
        });

        const shouldSaveSnapshot = nonArtifactChanges.some(
          (change) =>
            change.type === 'remove' ||
            (change.type === 'dimensions' && !change.resizing) ||
            (change.type === 'position' && !change.dragging),
        );

        if (shouldSaveSnapshot && !isRenderingSelectNode) {
          saveSnapshot();
        }
      },
      onEdgesChange: (changes: EdgeChange[]) => {
        const { setEdges, edges, setNodeResults, nodeResults, saveSnapshot } =
          get();

        // Create a deep copy of edges to ensure they are mutable
        const mutableEdges = edges.map((edge) => ({ ...edge }));
        setEdges(applyEdgeChanges(changes, mutableEdges) as CustomEdge[]);
        // Clear results for affected nodes when edges are removed
        changes.forEach((change) => {
          if (change.type === 'remove') {
            const edge = edges.find((e) => e.id === change.id);
            if (edge) {
              const newResults = { ...nodeResults };
              delete newResults[edge.target];
              setNodeResults(newResults as Record<string, InputValue>);
            }
          }
        });

        const shouldSaveSnapshot = changes.some(
          (change) => change.type === 'remove' || change.type === 'add',
        );

        if (shouldSaveSnapshot) {
          saveSnapshot();
        }
      },
      onEdgesDelete: (edgesToDelete: CustomEdge[]) => {
        const {
          setEdges,
          setNodeResults,
          nodeResults,
          edges,
          nodes,
          setNodes,
          saveSnapshot,
        } = get();

        edgesToDelete = edgesToDelete.filter((e) => !isArtifactEdge(e));
        if (edgesToDelete.length === 0) return;

        for (const edge of edgesToDelete) {
          const newResults = { ...nodeResults };
          if (edge.target) {
            delete newResults[edge.target];
          }
          setNodeResults(newResults as Record<string, InputValue>);
        }
        const remainingEdges = edges.filter(
          (edge) => !edgesToDelete.some((toDelete) => toDelete.id === edge.id),
        );
        setEdges(remainingEdges);

        // Update agent nodes when tool connections are removed
        const updatedNodes = updatePortOnEdgeDelete(edgesToDelete, nodes);
        setNodes(updatedNodes);

        // Save snapshot after deleting
        if (edgesToDelete.length > 0) {
          saveSnapshot();
        }
      },
      addAgentHandlePortConn: (
        nodes: CustomNode[],
        params: Connection,
        targetNode: CustomNode,
      ): CustomNode[] => {
        if (!targetNode) return nodes;

        const newConnection = {
          node_id: params.source as string,
          port_id: params.sourceHandle as string,
        };

        return nodes.map((node) => {
          if (node.id !== targetNode.id) return node;

          return {
            ...node,
            data: {
              ...node.data,
              struct: {
                ...node.data.struct,
                input_ports: node.data.struct.input_ports.map((port) => {
                  if (port.id !== params.targetHandle) return port;

                  return {
                    ...port,
                    incoming_connections: addIncomingConnection(
                      port.incoming_connections,
                      newConnection,
                    ),
                  };
                }),
              },
            },
          };
        });
      },
      addParamPortConn: (
        nodes: CustomNode[],
        targetNodeId: string,
        targetHandleId: string,
        sourceNodeId: string,
        sourceHandleId: string,
      ): CustomNode[] => {
        const targetNode = nodes.find((node) => node.id === targetNodeId);
        if (!targetNode) return nodes;

        const newConnection = {
          node_id: sourceNodeId,
          port_id: sourceHandleId,
        };

        return nodes.map((node) => {
          if (node.id !== targetNodeId) return node;

          return {
            ...node,
            data: {
              ...node.data,
              struct: {
                ...node.data.struct,
                params: node.data.struct.params.map((param) => {
                  if (param.bindable_config?.input_port.id !== targetHandleId) {
                    return { ...param };
                  }

                  const updatedConnections = addIncomingConnection(
                    param.bindable_config.input_port.incoming_connections,
                    newConnection,
                  );

                  return {
                    ...param,
                    bindable_config: {
                      ...param.bindable_config,
                      is_bound: true,
                      input_port: {
                        ...param.bindable_config.input_port,
                        incoming_connections: updatedConnections,
                      },
                    },
                  };
                }),
              },
            },
          };
        });
      },
      // Connection validation function
      validateConnection: (
        sourceNode: CustomNode,
        targetNode: CustomNode,
        sourceHandle: string | null,
        targetHandle: string | null,
      ): { isValid: boolean; error?: string } => {
        if (!sourceNode.data?.struct || !targetNode.data?.struct) {
          return { isValid: false, error: 'Invalid node' };
        }
        if (
          sourceNode.data.struct.react_flow_generated_id ===
          targetNode.data.struct.react_flow_generated_id
        ) {
          return {
            isValid: false,
            error: 'Source and target nodes cannot be the same',
          };
        }

        if (!sourceHandle || !targetHandle) {
          return { isValid: false, error: 'Missing source or target handle' };
        }

        // Find the source output port
        const sourcePort = sourceNode.data.struct.output_ports.find(
          (port) => port.id === sourceHandle,
        );
        if (!sourcePort) {
          return { isValid: false, error: 'Source port not found' };
        }

        // Find the target input port
        const targetPort =
          targetNode.data.struct.input_ports.find(
            (port) => port.id === targetHandle,
          ) ||
          targetNode.data.struct.params.find(
            (param) => param.bindable_config?.input_port.id === targetHandle,
          )?.bindable_config?.input_port;

        if (!targetPort) {
          return { isValid: false, error: 'Target port not found' };
        }

        // Check type compatibility
        const sourceTypes = sourcePort.port_type.accepted_types;
        const targetTypes = targetPort.port_type.accepted_types;

        const hasTypeMatch = sourceTypes.some((sourceType) =>
          targetTypes.includes(sourceType),
        );

        if (!hasTypeMatch) {
          return {
            isValid: false,
            error: `Type mismatch: source outputs ${sourceTypes.join(', ')} but target expects ${targetTypes.join(', ')}`,
          };
        }

        // Check cardinality for target port
        if (targetPort.cardinality === 'one') {
          // Check if there's already a connection to this target port
          const existingConnections = get().edges.filter(
            (edge) =>
              edge.target === targetNode.id &&
              edge.targetHandle === targetHandle,
          );

          if (existingConnections.length > 0) {
            return {
              isValid: false,
              error: `Target port only accepts one connection but already has ${existingConnections.length} connection(s)`,
            };
          }
        }

        return { isValid: true };
      },
      // Function to validate a connection from an edge/connection object
      // Used by Handle components and reconnection validation
      isValidEdgeConnection: (connection: Connection): boolean => {
        const { nodes, validateConnection } = get();

        // Get the source and target nodes
        const sourceNode = nodes.find(
          (node) => node.id === connection.source,
        ) as CustomNode | undefined;
        const targetNode = nodes.find(
          (node) => node.id === connection.target,
        ) as CustomNode | undefined;

        if (!sourceNode || !targetNode) return false;

        // Use the store's validateConnection function
        const validation = validateConnection(
          sourceNode,
          targetNode,
          connection.sourceHandle || null,
          connection.targetHandle || null,
        );

        return validation.isValid;
      },
      onConnect: (newConnection: Connection) => {
        const {
          setEdges,
          setNodeResults,
          edges,
          nodeResults,
          nodes,
          setNodes,
          addAgentHandlePortConn,
          addParamPortConn,
          saveSnapshot,
          // validateConnection,
        } = get();

        // Ensure we have a valid target and it's a string
        const target = newConnection.target;
        if (!target || typeof target !== 'string') {
          console.error('Invalid target for connection');
          return;
        }

        const sourceNode = nodes.find(
          (node) => node.id === newConnection.source,
        ) as CustomNode;
        const targetNode = nodes.find((node) => node.id === target);

        if (!sourceNode || !targetNode) {
          console.error('Source or target node not found');
          return;
        }

        // Clear the results of the target node when a new connection is made
        const newResults = { ...nodeResults };
        delete newResults[target];
        setNodeResults(newResults as Record<string, InputValue>);

        // Detect edge type and add the new edge
        const { edgeType, targetParamId } = detectEdgeType(
          newConnection.targetHandle,
          targetNode?.data.struct.params,
        );

        const edgeLayoutType =
          useEdgeLayoutStore.getState().layout === 'orthogonal'
            ? 'orthogonal'
            : 'default';

        setEdges(
          addEdge(
            {
              ...newConnection,
              sourceNodeId: sourceNode?.data.struct
                .react_flow_generated_id as string,
              targetNodeId: targetNode?.data.struct
                .react_flow_generated_id as string,
              source_node_id: newConnection.source,
              source_port_id: newConnection.sourceHandle as string,
              target_node_id: newConnection.target,
              target_port_id: newConnection.targetHandle as string,
              ...(targetParamId && { target_param_id: targetParamId }),
              type: edgeLayoutType,
              edge_type: edgeType,
              data: {},
            },
            edges,
          ),
        );

        let updatedNodes = nodes;

        // Update bindable config for the target field if it has one
        if (targetNode && newConnection.targetHandle && targetParamId) {
          updatedNodes = addParamPortConn(
            updatedNodes,
            target,
            newConnection.targetHandle,
            newConnection.source!,
            newConnection.sourceHandle!,
          );
        }

        // Update agent node when a tool is connected
        if (
          sourceNode?.data.struct.node_type === NodeCategory.TOOL &&
          targetNode?.data.struct.node_type === NodeCategory.AGENT
        ) {
          updatedNodes = addAgentHandlePortConn(
            updatedNodes,
            newConnection,
            targetNode,
          );
        }

        // Single setNodes call with all updates
        if (updatedNodes !== nodes) {
          setNodes(updatedNodes);
        }

        // Clear validation error for the connected field if it exists
        if (targetNode && newConnection.targetHandle && targetParamId) {
          const targetParam = targetNode.data.struct.params?.find(
            (p) => p.field_id === targetParamId,
          );
          if (targetParam) {
            const fieldId = targetParam.field_id;
            useWorkflowValidationStore
              .getState()
              .clearFieldError(target, fieldId);
          }
        }

        // Save snapshot after connecting
        saveSnapshot();
      },
      onReconnect: (
        oldEdge: CustomEdge,
        newConnection: Connection,
        edgeReconnectSuccessful: React.RefObject<boolean>,
      ) => {
        const {
          setEdges,
          setNodeResults,
          edges,
          nodeResults,
          nodes,
          setNodes,
          addAgentHandlePortConn,
          addParamPortConn,
          isValidEdgeConnection,
          saveSnapshot,
        } = get();

        // Validate the new connection before reconnecting
        if (!isValidEdgeConnection(newConnection)) {
          // Invalid connection - don't reconnect, will be cleaned up in onReconnectEnd
          return;
        }

        // Ensure we have a valid target and it's a string
        const target = newConnection.target;
        if (!target || typeof target !== 'string') {
          console.error('Invalid target for reconnection');
          return;
        }

        // Clear the results of the target node when a connection is reconnected
        const newResults = { ...nodeResults };
        delete newResults[target];
        // Also clear results for old target if it changed
        if (oldEdge.target && oldEdge.target !== target) {
          delete newResults[oldEdge.target];
        }
        setNodeResults(newResults as Record<string, InputValue>);

        // Clean up old connection's bindable config before reconnecting
        // Need to handle both source-end and target-end reconnections
        const targetChanged = oldEdge.target !== target;
        const targetHandleChanged =
          oldEdge.targetHandle !== newConnection.targetHandle;
        const sourceChanged = oldEdge.source !== newConnection.source;
        const sourceHandleChanged =
          oldEdge.sourceHandle !== newConnection.sourceHandle;

        let updatedNodes = nodes;

        // Clean up old port's incoming_connections if source or target changed
        // - Target changed: old target node has stale incoming_connection
        // - Source changed: current target node has incoming_connection pointing to old source
        // if so delete the incoming_connection from the old port
        if (
          (targetChanged ||
            targetHandleChanged ||
            sourceChanged ||
            sourceHandleChanged) &&
          oldEdge.target &&
          oldEdge.targetHandle
        ) {
          updatedNodes = updatePortOnEdgeDelete([oldEdge], nodes);
        }

        const sourceNode = updatedNodes.find(
          (node) => node.id === newConnection.source,
        ) as CustomNode;
        const targetNode = updatedNodes.find((node) => node.id === target);

        if (!sourceNode || !targetNode) {
          console.error('Source or target node not found for reconnection');
          return;
        }

        // Reconnect the edge using React Flow's utility
        const reconnectedEdges = reconnectEdge(oldEdge, newConnection, edges);

        // Detect edge type
        const { edgeType, targetParamId } = detectEdgeType(
          newConnection.targetHandle,
          targetNode?.data.struct.params,
        );

        // Update edge metadata
        // IMPORTANT: reconnectEdge may generate a new edge ID because React Flow creates IDs
        // based on source + sourceHandle + target + targetHandle. Since target/handle changed,
        // the ID changes. We need to find the edge by matching the new connection values.
        const updatedEdges = reconnectedEdges.map((edge) => {
          // Find the edge that matches the new connection (not the old edge ID)
          const isReconnectedEdge =
            edge.source === newConnection.source &&
            edge.target === newConnection.target &&
            edge.sourceHandle === newConnection.sourceHandle &&
            edge.targetHandle === newConnection.targetHandle;

          if (isReconnectedEdge || edge.id === oldEdge.id) {
            const newEdge = {
              ...edge,
              // Override all metadata fields explicitly
              sourceNodeId: sourceNode.data.struct
                .react_flow_generated_id as string,
              targetNodeId: targetNode.data.struct
                .react_flow_generated_id as string,
              source_node_id: newConnection.source!,
              source_port_id: newConnection.sourceHandle!,
              target_node_id: newConnection.target!,
              target_port_id: newConnection.targetHandle,
              target_param_id: targetParamId,
              edge_type: edgeType,
            } as CustomEdge;

            return newEdge;
          }
          return edge;
        });

        setEdges(updatedEdges);

        // Update bindable config for the new target field if it has one
        if (targetNode && newConnection.targetHandle && targetParamId) {
          updatedNodes = addParamPortConn(
            updatedNodes,
            target,
            newConnection.targetHandle,
            newConnection.source!,
            newConnection.sourceHandle!,
          );
        }

        // Update agent node when a tool is connected
        if (
          sourceNode?.data.struct.node_type === NodeCategory.TOOL &&
          targetNode?.data.struct.node_type === NodeCategory.AGENT
        ) {
          updatedNodes = addAgentHandlePortConn(
            updatedNodes,
            newConnection,
            targetNode,
          );
        }

        // Single setNodes call with all updates
        if (updatedNodes !== nodes) {
          setNodes(updatedNodes);
        }

        // Clear validation error for the reconnected field if it exists
        if (targetNode && newConnection.targetHandle && targetParamId) {
          const targetParam = targetNode.data.struct.params?.find(
            (p) => p.field_id === targetParamId,
          );
          if (targetParam) {
            const fieldId = targetParam.field_id;
            useWorkflowValidationStore
              .getState()
              .clearFieldError(target, fieldId);
          }
        }

        // Mark reconnection as successful (same pattern as langflow's onEdgeUpdate)
        edgeReconnectSuccessful.current = true;

        // Save snapshot after reconnecting
        saveSnapshot();
      },
      onReconnectStart: (edgeReconnectSuccessful: React.RefObject<boolean>) => {
        edgeReconnectSuccessful.current = false;
      },
      onReconnectEnd: (
        edgeReconnectSuccessful: React.RefObject<boolean>,
        edge: CustomEdge,
      ) => {
        const { setEdges, edges } = get();

        // If reconnection was not successful, clean up the edge
        if (!edgeReconnectSuccessful.current) {
          setEdges(edges.filter((e) => e.id !== edge.id));
          edgeReconnectSuccessful.current = true;
        }
      },
      addNode: (nodeStruct: NodeStruct, position, options) => {
        const { setNodes, nodes, selectNode, addToRecentlyUsed } = get();

        let nodePosition;

        if (position) {
          // Use explicitly provided position
          nodePosition = position;
        } else if (nodes.length === 0) {
          // First node: place at viewport center
          nodePosition = {
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
          };
        } else {
          // Subsequent nodes: place next to the last node with consistent spacing
          const lastNode = nodes[nodes.length - 1];
          nodePosition = {
            x:
              lastNode.position.x +
              (lastNode.measured?.width || DEFAULT_NODE_WIDTH) +
              NODE_HORIZONTAL_GAP,
            y: lastNode.position.y,
          };
        }

        // Use provided options or default to selected
        const nodeOptions = {
          selected: options?.selected ?? true,
          nodeId: options?.nodeId,
        };

        // Create node with synced ID using helper function
        const newNode = createNodeWithSyncedId(
          nodeStruct,
          nodePosition,
          nodes,
          nodeOptions,
        );

        // Deselect the currently selected nodes when adding new node
        const selectedCount = nodes.filter((n) => n.selected).length;
        const updatedNodes =
          nodeOptions.selected && selectedCount > 0
            ? nodes.map((node) =>
                node.selected ? { ...node, selected: false } : node,
              )
            : nodes;

        setNodes([...updatedNodes, newNode]);

        if (nodeOptions.selected) {
          selectNode(newNode.id);
        }

        // Add to recently used nodes
        addToRecentlyUsed({
          name: nodeStruct.display_name,
          nodeStruct: nodeStruct,
          icon: null, // Will be set by the context menu component
          lastUsed: Date.now(),
          category: nodeStruct.node_type,
        });

        // Auto-focus on newly added node if setCenter was provided (while maintaining the zoom level if provided)
        if (options?.setCenter) {
          const zoomLevel = options?.zoom ?? 1;
          // Offset the center to account for overlapping right sidebar (console sidebar)
          // so the node appears centered in the visible area rather than behind the sidebar
          const sidebarOffsetFlow = options?.rightSidebarWidthPx
            ? options.rightSidebarWidthPx / (2 * zoomLevel)
            : 0;
          options.setCenter(
            nodePosition.x + sidebarOffsetFlow,
            nodePosition.y,
            {
              duration: AUTO_FOCUS_ANIMATION_DURATION_MS,
              zoom: zoomLevel,
            },
          );
        }

        // Return the created node ID for connection purposes
        return newNode.id;
      },
      addNodes: (entries) => {
        const { setNodes, nodes, addToRecentlyUsed } = get();
        if (entries.length === 0) return [];

        // Deselect all existing nodes
        const deselectedNodes = nodes.map((n) =>
          n.selected ? { ...n, selected: false } : n,
        );

        // Build all new nodes, passing cumulative list for auto-name deduplication
        const allExisting = [...deselectedNodes];
        const newNodes: CustomNode[] = [];
        entries.forEach(({ struct, position }) => {
          const node = createNodeWithSyncedId(
            struct,
            position,
            [...allExisting, ...newNodes],
            { selected: true },
          );
          newNodes.push(node);
        });

        // Single state update with all nodes
        setNodes([...deselectedNodes, ...newNodes]);

        // Track the last node for console panel selection
        const lastNodeId = newNodes[newNodes.length - 1].id;
        set({ lastSelectedNodeId: lastNodeId });
        useWorkflowPanelStore.getState().setConsolePanelOpen(true);

        // Add all to recently used
        entries.forEach(({ struct }) => {
          addToRecentlyUsed({
            name: struct.display_name,
            nodeStruct: struct,
            icon: null,
            lastUsed: Date.now(),
            category: struct.node_type,
          });
        });

        return newNodes.map((n) => n.id);
      },
      addStickyNote: (position, options) => {
        const { setNodes, nodes, saveSnapshot } = get();
        const newNodeId = uuidv4();
        const backgroundColor =
          options?.backgroundColor ?? DEFAULT_STICKY_NOTE_COLOR;

        const newNode: CustomNode = {
          id: newNodeId,
          type: NodeType.StickyNoteNode,
          position,
          width: STICKY_NOTE_DEFAULT_WIDTH,
          height: STICKY_NOTE_DEFAULT_HEIGHT,
          zIndex: STICKY_NOTE_Z_INDEX,
          // Mark selected so the floating toolbar / resize handles render,
          // but do NOT call selectNode() — that opens the console panel,
          // which is intentionally hidden for sticky notes.
          selected: true,
          data: {
            struct: createStickyNoteStruct(newNodeId),
            text: options?.text ?? '',
            backgroundColor,
          } as StickyNoteData,
        };

        // Deselect other nodes (mirrors addNode's behaviour) but do NOT call
        // selectNode(), which would open the console panel. We also close
        // the panel here so a previously-open Configure tab for a different
        // node doesn't stay visible.
        const deselected = nodes.map((n) =>
          n.selected ? { ...n, selected: false } : n,
        );
        setNodes([...deselected, newNode]);
        set({ lastSelectedNodeId: newNodeId });
        useWorkflowPanelStore.getState().setConsolePanelOpen(false);

        saveSnapshot();
        return newNodeId;
      },
      updateStickyNoteData: (nodeId, patch) => {
        const { nodes, setNodes, saveSnapshot } = get();
        const target = nodes.find((n) => n.id === nodeId);
        if (!target || target.type !== NodeType.StickyNoteNode) return;

        const dimensionPatch: { width?: number; height?: number } = {};
        const dataPatch = { ...patch };

        const updatedNodes = nodes.map((node) => {
          if (node.id !== nodeId) return node;
          return {
            ...node,
            ...dimensionPatch,
            data: {
              ...(node.data ?? {}),
              ...dataPatch,
            } as StickyNoteData,
          } as CustomNode;
        });

        setNodes(updatedNodes);

        // Text edits should not push undo snapshots per-keystroke (they'd
        // overwhelm the history). Color changes are discrete and worth snapshotting.
        const isStructuralChange = patch.backgroundColor !== undefined;
        if (isStructuralChange) saveSnapshot();
      },
      dragOverNodeId: null,
      setDragOverNodeId: (nodeId: string | null) => {
        if (get().dragOverNodeId !== nodeId) {
          set({ dragOverNodeId: nodeId });
        }
      },
      onDragOver: (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      },
      onDrop: (
        event: React.DragEvent<HTMLDivElement>,
        dndPayload: DnDPayload,
        screenToFlowPosition: (pos: { x: number; y: number }) => {
          x: number;
          y: number;
        },
        dropTargetNodeId?: string,
      ) => {
        event.preventDefault();
        set({ dragOverNodeId: null });
        const { groupedNodes, addNode, addNodes, onConnect, nodes } = get();

        if (!dndPayload) return undefined;

        const findNodeById = (nodeId: string): NodeStruct | undefined => {
          for (const nodeCategory in groupedNodes) {
            const found = groupedNodes[nodeCategory].find(
              (node) => node.node_id === nodeId,
            );
            if (found) return found;
          }
          return undefined;
        };

        const targetNode = dropTargetNodeId
          ? nodes.find((n) => n.id === dropTargetNodeId)
          : undefined;
        const targetStruct = targetNode?.data?.struct;
        // New catalog nodes are always added at canvas-root (no parentId),
        // so the drop position must be absolute — but targetNode.position is
        // parent-relative if the drop target is inside a group. Resolve its
        // absolute position before using it for placement.
        const targetNodeAbsolute = targetNode
          ? {
              ...targetNode,
              position: getAbsolutePosition(
                targetNode,
                new Map(nodes.map((n) => [n.id, n])),
              ),
            }
          : undefined;

        const cursorPosition = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        // Resolve node IDs from the payload
        const payloadNodeIds =
          dndPayload.type === 'node' ? [dndPayload.nodeId] : dndPayload.nodeIds;
        const nodeStructs = payloadNodeIds
          .map(findNodeById)
          .filter((n): n is NodeStruct => n !== undefined);

        if (nodeStructs.length === 0) return undefined;

        // Determine connection direction from the first node for positioning
        const connectionResult = targetStruct
          ? resolveAutoConnection(nodeStructs[0], targetStruct)
          : null;
        const dropPosition = calculateDropPosition(
          cursorPosition,
          targetNodeAbsolute,
          nodeStructs.length,
          connectionResult?.direction,
        );

        const { isValidEdgeConnection } = get();

        const connectAll = (nodeIds: string[], structs: NodeStruct[]) => {
          nodeIds.forEach((nodeId, index) => {
            applyAutoConnection(
              nodeId,
              structs[index],
              targetNode,
              targetStruct,
              onConnect,
              isValidEdgeConnection,
            );
          });
        };

        if (nodeStructs.length === 1) {
          const newNodeId = addNode(nodeStructs[0], dropPosition);
          // Suppress onConnect snapshots so the entire drop is one undo step.
          // React Flow's onNodesChange('dimensions') will save the "after" snapshot.
          withSuppressedSnapshots(() => connectAll([newNodeId], nodeStructs));
          return newNodeId;
        }

        const entries = nodeStructs.map((struct, index) => ({
          struct,
          position: {
            x: dropPosition.x,
            y: dropPosition.y + index * VERTICAL_STACK_GAP,
          },
        }));
        const newNodeIds = addNodes(entries);
        withSuppressedSnapshots(() => connectAll(newNodeIds, nodeStructs));
        return newNodeIds[newNodeIds.length - 1];
      },
      getCompatibleNodes: (nodeStruct: NodeStruct) => {
        const { groupedNodes } = get();
        const compatibleNodesRecord: NodeStruct[] = [];

        // Get all output ports from the given node
        const outputPorts = nodeStruct.output_ports || [];

        // If the node has no output ports, return empty array
        if (outputPorts.length === 0) {
          return compatibleNodesRecord;
        }

        // Iterate through all available nodes in groupedNodes
        Object.values(groupedNodes).forEach((categoryNodes) => {
          categoryNodes.forEach((candidateNode) => {
            if (isPickerHiddenNode(candidateNode)) return;

            // Get all input ports from the candidate node (including bindable params)
            const inputPorts = candidateNode.input_ports || [];
            const bindableInputPorts =
              candidateNode.params
                ?.filter((param) => param.bindable_config?.input_port)
                .map((param) => param.bindable_config!.input_port) || [];

            const allInputPorts = [...inputPorts, ...bindableInputPorts];

            // Check if any output port of the given node is compatible with any input port of the candidate node
            const isCompatible = checkForAnyCompatiblePorts(
              outputPorts,
              allInputPorts,
            );

            if (isCompatible) {
              compatibleNodesRecord.push(candidateNode);
            }
          });
        });

        return compatibleNodesRecord;
      },

      createSelectNodeForConnection: ({
        fromNodeId,
        fromHandleId,
        fromHandleType,
        position,
        compatibleNodes,
        acceptedTypes,
      }) => {
        const { nodes, edges, setNodes, setEdges } = get();

        const selectNodeId = uuidv4();
        const newEdgeId = uuidv4();
        const isDraggingFromInput = fromHandleType === 'target';

        // Drop any existing SelectNode wired to the same handle so re-drags
        // replace rather than accumulate SelectNodes.
        const edgesToExistingSelectNodes = edges.filter((edge) => {
          const isConnectedToSameHandle = isDraggingFromInput
            ? edge.target === fromNodeId && edge.targetHandle === fromHandleId
            : edge.source === fromNodeId && edge.sourceHandle === fromHandleId;
          const attachedNodeId = isDraggingFromInput
            ? edge.source
            : edge.target;
          const isSelectNode = nodes.some(
            (node) => node.id === attachedNodeId && node.type === 'SelectNode',
          );
          return isConnectedToSameHandle && isSelectNode;
        });

        const existingSelectNodeIds = new Set(
          edgesToExistingSelectNodes.map((edge) =>
            isDraggingFromInput ? edge.source : edge.target,
          ),
        );

        const filteredNodes = nodes.filter(
          (node) => !existingSelectNodeIds.has(node.id),
        );
        const filteredEdges = edges.filter(
          (edge) =>
            !existingSelectNodeIds.has(edge.source) &&
            !existingSelectNodeIds.has(edge.target),
        );

        const selectNode: CustomNode = {
          id: selectNodeId,
          type: NodeType.SelectNode,
          position,
          data: {
            struct: SELECT_NODE_PLACEHOLDER_STRUCT,
            compatibleNodes,
            // acceptedTypes comes from port_type.accepted_types which is
            // HandleType[] at runtime; the wider string[] typing from the
            // event-computation path requires the cast here.
            acceptedTypes:
              acceptedTypes as import('@/types/node-types').HandleType[],
            type: fromHandleType,
            ...(isDraggingFromInput && {
              targetNodeId: fromNodeId,
              targetHandleId: fromHandleId ?? undefined,
            }),
          },
        };

        setNodes([...filteredNodes, selectNode]);

        const edgeLayoutType =
          useEdgeLayoutStore.getState().layout === 'orthogonal'
            ? 'orthogonal'
            : 'default';

        // Look up the catalog node id of the from-node (used by the save
        // pipeline) — must match the node_id format select-node.tsx writes
        // when it later finalizes the SelectNode into a real node.
        const fromNode = nodes.find((n) => n.id === fromNodeId);
        const fromStructId = (fromNode?.data.struct as NodeStruct | undefined)
          ?.node_id as string | undefined;

        // fromHandleId may be null (React Flow uses null for "no handle id").
        // CustomEdge.targetHandle / sourceHandle are typed as string (not
        // string | null), so coerce null → undefined to satisfy the type.
        const handleId = fromHandleId ?? undefined;

        // CustomEdge.targetHandle / sourceHandle are typed string (not
        // string | null), so we cast the literals as CustomEdge matching the
        // pattern used throughout the rest of the codebase.
        const newEdge: CustomEdge = (
          isDraggingFromInput
            ? {
                id: newEdgeId,
                source: selectNodeId,
                target: fromNodeId,
                ...(fromStructId && { targetNodeId: fromStructId }),
                target_node_id: fromNodeId,
                target_port_id: handleId,
                sourceHandle: 'select-node-source-handle',
                targetHandle: handleId,
                type: edgeLayoutType,
                edge_type: ConnectionType.PORT_TO_PORT,
                data: {},
              }
            : {
                id: newEdgeId,
                source: fromNodeId,
                target: selectNodeId,
                ...(fromStructId && { sourceNodeId: fromStructId }),
                source_node_id: fromNodeId,
                source_port_id: handleId,
                sourceHandle: handleId,
                type: edgeLayoutType,
                edge_type: ConnectionType.PORT_TO_PORT,
                data: {},
              }
        ) as CustomEdge;

        setEdges([...filteredEdges, newEdge]);

        return selectNodeId;
      },

      getCompatibleSourceNodes: (
        nodeStruct: NodeStruct,
        inputPortId?: string,
      ) => {
        const { groupedNodes } = get();
        const compatibleNodesRecord: NodeStruct[] = [];

        // Get input ports from the given node (including bindable params)
        const inputPorts = nodeStruct.input_ports || [];
        const bindableInputPorts =
          nodeStruct.params
            ?.filter((param) => param.bindable_config?.input_port)
            .map((param) => param.bindable_config!.input_port) || [];

        let allInputPorts = [...inputPorts, ...bindableInputPorts];

        // If a specific input port ID is provided, filter to only that port
        if (inputPortId) {
          allInputPorts = allInputPorts.filter(
            (port) => port.id === inputPortId,
          );
        }

        // If the node has no input ports, return empty array
        if (allInputPorts.length === 0) {
          return compatibleNodesRecord;
        }

        // Iterate through all available nodes in groupedNodes
        Object.values(groupedNodes).forEach((categoryNodes) => {
          categoryNodes.forEach((candidateNode) => {
            if (isPickerHiddenNode(candidateNode)) return;

            // Get all output ports from the candidate node
            const outputPorts = candidateNode.output_ports || [];

            // Check if any output port of the candidate node is compatible with any input port of the given node
            const isCompatible = checkForAnyCompatiblePorts(
              outputPorts,
              allInputPorts,
            );

            if (isCompatible) {
              compatibleNodesRecord.push(candidateNode);
            }
          });
        });

        return compatibleNodesRecord;
      },
    };
  }),
);

/**
 * Assigns fresh React Flow IDs to a set of nodes and their corresponding
 * edges, clears stale incoming_connections, then re-builds them from the
 * edges. This is the shared core for paste and import operations.
 *
 * @param options.offset  When provided, node positions are computed relative
 *   to the first node so the group lands at the given canvas coordinate.
 *   When omitted, original positions are preserved (import case).
 * @param options.selected  Selected state applied to every returned node/edge.
 */
export function remapNodesAndEdges(
  nodes: CustomNode[],
  edges: CustomEdge[],
  options: { offset?: { x: number; y: number }; selected?: boolean } = {},
): { nodes: CustomNode[]; edges: CustomEdge[] } {
  const { offset, selected = false } = options;
  const firstNodePosition = nodes[0]?.position ?? { x: 0, y: 0 };

  // First pass: new IDs + clean stale connections
  const idMap = new Map<string, string>();
  let newNodes: CustomNode[] = nodes.map((node) => {
    const newId = uuidv4();
    idMap.set(node.id, newId);

    const cleanedParams = node.data.struct?.params.map((param) => {
      if (!param.bindable_config?.input_port) return param;
      return {
        ...param,
        bindable_config: {
          ...param.bindable_config,
          is_bound: false,
          input_port: {
            ...param.bindable_config.input_port,
            incoming_connections: EMPTY_CONNECTION,
          },
        },
      };
    });

    const cleanedInputPorts = node.data.struct?.input_ports.map((port) => ({
      ...port,
      incoming_connections: EMPTY_CONNECTION,
    }));

    const position = offset
      ? {
          x: offset.x + (node.position.x - firstNodePosition.x),
          y: offset.y + (node.position.y - firstNodePosition.y),
        }
      : node.position;

    return {
      ...node,
      id: newId,
      position,
      data: {
        ...node.data,
        struct: node.data.struct
          ? {
              ...node.data.struct,
              react_flow_generated_id: newId,
              params: cleanedParams ?? node.data.struct.params,
              input_ports: cleanedInputPorts ?? node.data.struct.input_ports,
            }
          : node.data.struct,
      },
      selected,
    };
  });

  newNodes = newNodes.map((node) => {
    if (!node.parentId) return node;
    const remappedParentId = idMap.get(node.parentId);
    return {
      ...node,
      parentId: remappedParentId,
    };
  });

  // Remap edges — only keep edges whose both endpoints were in the input set
  const newEdges: CustomEdge[] = edges
    .filter((e) => idMap.has(e.source) && idMap.has(e.target))
    .map((e) => {
      const newSource = idMap.get(e.source)!;
      const newTarget = idMap.get(e.target)!;
      return {
        ...e,
        id: uuidv4(),
        source: newSource,
        target: newTarget,
        sourceNodeId: newSource,
        targetNodeId: newTarget,
        source_node_id: newSource,
        target_node_id: newTarget,
        type: e.type || 'default',
        data: e.data || {},
        selected,
      };
    });

  // Second pass: re-build incoming_connections from the new edges
  const { addParamPortConn, addAgentHandlePortConn } = useNodeStore.getState();

  newEdges.forEach((edge) => {
    const sourceNode = newNodes.find((n) => n.id === edge.source);
    const targetNode = newNodes.find((n) => n.id === edge.target);

    if (!sourceNode || !targetNode || !edge.targetHandle) return;

    const targetParam = targetNode.data.struct?.params?.find(
      (p) => p.bindable_config?.input_port?.id === edge.targetHandle,
    );

    if (targetParam) {
      newNodes = addParamPortConn(
        newNodes,
        edge.target,
        edge.targetHandle,
        edge.source,
        edge.sourceHandle!,
      );
    }

    if (
      sourceNode.data.struct?.node_type === NodeCategory.TOOL &&
      targetNode.data.struct?.node_type === NodeCategory.AGENT
    ) {
      newNodes = addAgentHandlePortConn(
        newNodes,
        {
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle!,
          targetHandle: edge.targetHandle!,
        },
        targetNode,
      );
    }
  });

  return { nodes: newNodes, edges: newEdges };
}

// Utility function to update ports when edges are deleted or reconnected
// Returns updated nodes array - caller is responsible for calling setNodes
export const updatePortOnEdgeDelete = (
  edgesToDelete: CustomEdge[],
  nodes: CustomNode[],
): CustomNode[] => {
  if (edgesToDelete.length === 0) return nodes;

  // Create a map of target nodes and their affected handles
  const targetNodeConnections = new Map<string, Set<string>>();

  edgesToDelete.forEach((edge) => {
    if (edge.target && edge.targetHandle) {
      if (!targetNodeConnections.has(edge.target)) {
        targetNodeConnections.set(edge.target, new Set());
      }
      targetNodeConnections.get(edge.target)!.add(edge.targetHandle!);
    }
  });

  // Update nodes to remove deleted connections
  const updatedNodes = nodes.map((node) => {
    if (!targetNodeConnections.has(node.id)) return node;

    const deletedHandles = targetNodeConnections.get(node.id)!;

    return {
      ...node,
      data: {
        ...node.data,
        struct: {
          ...node.data.struct,
          // Clean up input_ports
          input_ports: node.data.struct.input_ports.map((port) => {
            if (!deletedHandles.has(port.id)) return port;

            return {
              ...port,
              incoming_connections: removeIncomingConnections(
                port.incoming_connections,
                edgesToDelete,
              ),
            };
          }),
          // Clean up params with bindable_config
          params: node.data.struct.params.map((param) => {
            if (
              !param.bindable_config?.input_port ||
              !deletedHandles.has(param.bindable_config.input_port.id)
            ) {
              return { ...param };
            }

            const updatedConnections = removeIncomingConnections(
              param.bindable_config.input_port.incoming_connections,
              edgesToDelete,
            );

            return {
              ...param,
              bindable_config: {
                ...param.bindable_config,
                is_bound: hasNonEmptyConnections(updatedConnections),
                input_port: {
                  ...param.bindable_config.input_port,
                  incoming_connections: updatedConnections,
                },
              },
            };
          }),
        },
      },
    };
  });

  return updatedNodes;
};

export interface WorkflowRepairResult {
  repairedNodes: CustomNode[];
  repairedEdges: CustomEdge[];
  repairsMade: boolean;
  details: {
    removedConnectionsCount: number;
    removedEdgesCount: number;
    fixedEdgeTypesCount: number;
    addedConnectionsCount: number;
  };
}

/**
 * Repairs orphaned references in workflow nodes and edges.
 *
 * This function provides comprehensive repair for corrupted workflow data,
 * fixing all types of orphaned references detected by validateOrphanedReferences.
 *
 * Fixes:
 * - Orphaned incoming_connections (input_ports & bindable params)
 * - Problematic edges (missing nodes/ports)
 * - Incorrect edge types (PORT_TO_PARAM vs PORT_TO_PORT)
 * - Incorrect target_param_id values
 * - Missing incoming_connections from valid edges
 * - Inconsistent is_bound state
 *
 * @param nodes - Current workflow nodes
 * @param edges - Current workflow edges
 * @param validationResult - Optional pre-computed validation result (avoids re-validation)
 * @returns Repair result with fixed nodes/edges and details of repairs made
 */
export const repairWorkflowOrphanedReferences = (
  nodes: CustomNode[],
  edges: CustomEdge[],
  validationResult?: OrphanedReferenceValidationResult,
): WorkflowRepairResult => {
  // If validation not provided, check if repairs are needed
  const validation =
    validationResult || validateOrphanedReferences(nodes, edges);

  // If no issues detected, return original data
  if (validation.isValid) {
    return {
      repairedNodes: nodes,
      repairedEdges: edges,
      repairsMade: false,
      details: {
        removedConnectionsCount: 0,
        removedEdgesCount: 0,
        fixedEdgeTypesCount: 0,
        addedConnectionsCount: 0,
      },
    };
  }

  // Run comprehensive cleanup from workflow-cleanup.ts
  const cleanupResult = cleanupWorkflow(nodes, edges);

  // Log repair details to console for developer awareness
  console.warn(
    '[repairWorkflowOrphanedReferences] Auto-repaired workflow corruption:',
    {
      removedConnections: cleanupResult.removedConnectionsCount,
      removedEdges: cleanupResult.removedEdgesCount,
      fixedEdgeTypes: cleanupResult.fixedEdgeTypesCount,
      validationErrors: validation.errors.length,
    },
  );

  return {
    repairedNodes: cleanupResult.cleanedNodes,
    repairedEdges: cleanupResult.cleanedEdges,
    repairsMade: true,
    details: {
      removedConnectionsCount: cleanupResult.removedConnectionsCount,
      removedEdgesCount: cleanupResult.removedEdgesCount,
      fixedEdgeTypesCount: cleanupResult.fixedEdgeTypesCount,
      addedConnectionsCount: 0, // cleanupWorkflow includes this in synchronization
    },
  };
};

// =============================================================================
// Orphaned Reference Validation
// =============================================================================

export interface OrphanedReferenceError {
  type: 'edge' | 'incoming_connection' | 'edge_type';
  edgeId?: string;
  nodeId?: string;
  nodeName?: string;
  portId?: string;
  paramId?: string;
  referencedNodeId: string;
  referencedPortId?: string;
  message: string;
}

export interface OrphanedReferenceValidationResult {
  isValid: boolean;
  errors: OrphanedReferenceError[];
}

/**
 * Validates that all node and edge references in the workflow are valid.
 * Detects:
 * 1. Edges that reference non-existent source or target nodes
 * 2. Edges with invalid source or target handles (port IDs)
 * 3. Edges with incorrect edge_type (PORT_TO_PARAM vs PORT_TO_PORT)
 * 4. Edges with incorrect target_param_id
 * 5. incoming_connections in input_ports that reference non-existent nodes
 * 6. incoming_connections in bindable_config that reference non-existent nodes
 *
 * @param nodes Array of workflow nodes
 * @param edges Array of workflow edges
 * @returns Validation result with any orphaned reference errors
 */
export const validateOrphanedReferences = (
  nodes: CustomNode[],
  edges: CustomEdge[],
): OrphanedReferenceValidationResult => {
  const errors: OrphanedReferenceError[] = [];

  // Create a set of valid node IDs for quick lookup
  const validNodeIds = new Set(nodes.map((node) => node.id));

  // Create a map of node ID to node for detailed error messages
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));

  // Create a map of node ID to its valid port IDs (both input and output)
  const nodePortMap = new Map<
    string,
    { inputPorts: Set<string>; outputPorts: Set<string> }
  >();
  nodes.forEach((node) => {
    const inputPorts = new Set<string>();
    const outputPorts = new Set<string>();

    // Add input port IDs
    node.data.struct?.input_ports?.forEach((port) => {
      inputPorts.add(port.id);
    });

    // Add bindable param port IDs as input ports
    node.data.struct?.params?.forEach((param) => {
      if (param.bindable_config?.input_port?.id) {
        inputPorts.add(param.bindable_config.input_port.id);
      }
    });

    // Add output port IDs
    node.data.struct?.output_ports?.forEach((port) => {
      outputPorts.add(port.id);
    });

    nodePortMap.set(node.id, { inputPorts, outputPorts });
  });

  // ==========================================================================
  // 1. Validate edges - check source and target node references
  // ==========================================================================
  edges.forEach((edge) => {
    // Check if source node exists
    if (!validNodeIds.has(edge.source)) {
      errors.push({
        type: 'edge',
        edgeId: edge.id,
        referencedNodeId: edge.source,
        referencedPortId: edge.sourceHandle || undefined,
        message: `Edge references non-existent source node: ${edge.source}`,
      });
    } else if (edge.sourceHandle) {
      // Source node exists, check if source handle (output port) is valid
      const ports = nodePortMap.get(edge.source);
      if (ports && !ports.outputPorts.has(edge.sourceHandle)) {
        const sourceNode = nodeMap.get(edge.source);
        errors.push({
          type: 'edge',
          edgeId: edge.id,
          nodeId: edge.source,
          nodeName: sourceNode?.data.struct?.display_name,
          referencedNodeId: edge.source,
          referencedPortId: edge.sourceHandle,
          message: `Edge references non-existent output port "${edge.sourceHandle}" on node "${sourceNode?.data.struct?.display_name || edge.source}"`,
        });
      }
    }

    // Check if target node exists
    if (!validNodeIds.has(edge.target)) {
      errors.push({
        type: 'edge',
        edgeId: edge.id,
        referencedNodeId: edge.target,
        referencedPortId: edge.targetHandle || undefined,
        message: `Edge references non-existent target node: ${edge.target}`,
      });
    } else if (edge.targetHandle) {
      // Target node exists, check if target handle (input port) is valid
      const ports = nodePortMap.get(edge.target);
      const targetNode = nodeMap.get(edge.target);
      if (ports && !ports.inputPorts.has(edge.targetHandle)) {
        errors.push({
          type: 'edge',
          edgeId: edge.id,
          nodeId: edge.target,
          nodeName: targetNode?.data.struct?.display_name,
          referencedNodeId: edge.target,
          referencedPortId: edge.targetHandle,
          message: `Edge references non-existent input port "${edge.targetHandle}" on node "${targetNode?.data.struct?.display_name || edge.target}"`,
        });
      } else if (targetNode) {
        // The handle points at a real port, so its binding metadata can be
        // checked. An edge landing on a param's input port must be typed
        // PORT_TO_PARAM and carry that param's field_id; otherwise the runtime
        // hands the target node nothing and every downstream node sees None.
        // Check if targetHandle matches a bindable param's input_port.id
        const matchingParam = targetNode.data.struct?.params?.find(
          (param) =>
            param.bindable_config?.input_port?.id === edge.targetHandle,
        );

        const isPortToParam = !!matchingParam;
        const correctEdgeType = isPortToParam
          ? ConnectionType.PORT_TO_PARAM
          : ConnectionType.PORT_TO_PORT;
        // For PORT_TO_PARAM: must be the param's field_id
        // For PORT_TO_PORT: can be null or undefined (both are acceptable)
        const correctTargetParamId = isPortToParam
          ? matchingParam.field_id
          : null;

        // Validate edge_type
        if (edge.edge_type !== correctEdgeType) {
          errors.push({
            type: 'edge_type',
            edgeId: edge.id,
            nodeId: edge.target,
            nodeName: targetNode.data.struct?.display_name,
            portId: edge.targetHandle,
            paramId: matchingParam?.field_id,
            referencedNodeId: edge.target,
            referencedPortId: edge.targetHandle,
            message: `Edge has incorrect edge_type: expected "${correctEdgeType}" but got "${edge.edge_type}". Target handle "${edge.targetHandle}" ${
              isPortToParam
                ? `matches bindable param "${matchingParam?.field_id}"`
                : 'does not match any bindable param'
            }`,
          });
        }

        // Validate target_param_id when edge_type is PORT_TO_PARAM
        // For PORT_TO_PARAM edges, target_param_id must match the param's field_id
        if (
          edge.edge_type === ConnectionType.PORT_TO_PARAM &&
          edge.target_param_id !== correctTargetParamId
        ) {
          errors.push({
            type: 'edge_type',
            edgeId: edge.id,
            nodeId: edge.target,
            nodeName: targetNode.data.struct?.display_name,
            portId: edge.targetHandle,
            paramId: matchingParam?.field_id,
            referencedNodeId: edge.target,
            referencedPortId: edge.targetHandle,
            message: `Edge has incorrect target_param_id: expected "${correctTargetParamId}" but got "${edge.target_param_id}"`,
          });
        }

        // Validate target_param_id is null or undefined when edge_type is PORT_TO_PORT
        // (both null and undefined are acceptable for PORT_TO_PORT edges)
        if (
          edge.edge_type === ConnectionType.PORT_TO_PORT &&
          edge.target_param_id !== null &&
          edge.target_param_id !== undefined
        ) {
          errors.push({
            type: 'edge_type',
            edgeId: edge.id,
            nodeId: edge.target,
            nodeName: targetNode.data.struct?.display_name,
            portId: edge.targetHandle,
            referencedNodeId: edge.target,
            referencedPortId: edge.targetHandle,
            message: `Edge has incorrect target_param_id: expected null or undefined for PORT_TO_PORT edge but got "${edge.target_param_id}"`,
          });
        }
      }
    }
  });

  // ==========================================================================
  // 2. Validate incoming_connections in input_ports
  // ==========================================================================
  nodes.forEach((node) => {
    const nodeStruct = node.data.struct;
    if (!nodeStruct) return;

    // Check input_ports incoming_connections
    nodeStruct.input_ports?.forEach((port) => {
      port.incoming_connections?.forEach((conn) => {
        // Skip empty placeholder connections
        if (!conn.node_id || conn.node_id === '') return;

        if (!validNodeIds.has(conn.node_id)) {
          errors.push({
            type: 'incoming_connection',
            nodeId: node.id,
            nodeName: nodeStruct.display_name,
            portId: port.id,
            referencedNodeId: conn.node_id,
            referencedPortId: conn.port_id,
            message: `Node "${nodeStruct.display_name}" has input port "${port.display_name}" with connection to non-existent node: ${conn.node_id}`,
          });
        } else if (conn.port_id) {
          // Referenced node exists, check if the port exists on it
          const refPorts = nodePortMap.get(conn.node_id);
          if (refPorts && !refPorts.outputPorts.has(conn.port_id)) {
            const refNode = nodeMap.get(conn.node_id);
            errors.push({
              type: 'incoming_connection',
              nodeId: node.id,
              nodeName: nodeStruct.display_name,
              portId: port.id,
              referencedNodeId: conn.node_id,
              referencedPortId: conn.port_id,
              message: `Node "${nodeStruct.display_name}" has input port "${port.display_name}" with connection to non-existent output port "${conn.port_id}" on node "${refNode?.data.struct?.display_name || conn.node_id}"`,
            });
          }
        }
      });
    });

    // Check params with bindable_config incoming_connections
    nodeStruct.params?.forEach((param) => {
      if (!param.bindable_config?.input_port?.incoming_connections) return;

      param.bindable_config.input_port.incoming_connections.forEach((conn) => {
        // Skip empty placeholder connections
        if (!conn.node_id || conn.node_id === '') return;

        if (!validNodeIds.has(conn.node_id)) {
          errors.push({
            type: 'incoming_connection',
            nodeId: node.id,
            nodeName: nodeStruct.display_name,
            paramId: param.field_id,
            portId: param.bindable_config?.input_port?.id,
            referencedNodeId: conn.node_id,
            referencedPortId: conn.port_id,
            message: `Node "${nodeStruct.display_name}" has parameter "${param.display_name}" with connection to non-existent node: ${conn.node_id}`,
          });
        } else if (conn.port_id) {
          // Referenced node exists, check if the port exists on it
          const refPorts = nodePortMap.get(conn.node_id);
          if (refPorts && !refPorts.outputPorts.has(conn.port_id)) {
            const refNode = nodeMap.get(conn.node_id);
            errors.push({
              type: 'incoming_connection',
              nodeId: node.id,
              nodeName: nodeStruct.display_name,
              paramId: param.field_id,
              portId: param.bindable_config?.input_port?.id,
              referencedNodeId: conn.node_id,
              referencedPortId: conn.port_id,
              message: `Node "${nodeStruct.display_name}" has parameter "${param.display_name}" with connection to non-existent output port "${conn.port_id}" on node "${refNode?.data.struct?.display_name || conn.node_id}"`,
            });
          }
        }
      });
    });
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};
